import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from "react";
import ReactDOM from "react-dom";
import "./App.css";
import { useAuth } from "./auth/AuthContext";

import { FiBox, FiImage } from "react-icons/fi";
import { IoCloseCircleOutline, IoSettingsOutline, IoStop } from "react-icons/io5";
import { HiVolumeUp, HiVolumeOff } from "react-icons/hi";

import { motion, AnimatePresence } from "framer-motion"; // You'll need to install framer-motion
import { RxAvatar } from "react-icons/rx";
import { FaCheck, FaLink } from "react-icons/fa";
import { PiShirtFoldedDuotone } from "react-icons/pi";

import env1Url from "./env1.jpg"

// React Three Fiber imports
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

import { HiOutlinePhone, HiOutlinePhoneXMark } from "react-icons/hi2";
import { RiVoiceprintFill } from "react-icons/ri";
import { LuBrainCog } from "react-icons/lu";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { LuMessagesSquare } from "react-icons/lu";

import Model from "./Model.js";
// import Model from "./ModelAnimation.js";

import LoadingDiv from "./components/LoadingDiv";
import CollapsibleMemoriesPanel from "./components/CollapsiblePanel.js";
import Chat from "./components/Chat.js";
import { formatDateSeparator, formatDuration, needsTodaySeparator } from "./utils.js";
import MemoryCard from "./components/MemoryCard.js";
import MemoryManager from "./components/MemoryManager.js";
import Switch from "./components/Switch.js";

import { FiUser } from "react-icons/fi";
import LoadingDots from "./components/LoadingDots.js";
import Overlay from "./components/Overlay.js";
import EmailVerificationOverlay from "./components/EmailVerificationOverlay";
import SoulOrb from "./components/SoulOrb";
import PrivacyPanel from "./components/PrivacyPanel";
import WisdomArchive from "./components/WisdomArchive";
import { BsArrowRight } from "react-icons/bs";
import PrivacyPolicy from "./PrivacyPolicy.js";
import SaveDialog from "./components/SaveDialog.js";
import { IoIosLogOut } from "react-icons/io";
import { IoIosCloseCircleOutline } from "react-icons/io";
import MyPanelWithWaves from "./components/WavyPanel.js";
import EnvironmentModal from "./components/EnvironmentModal.js";
import usePreloadAssets from "./hooks/ResourcePreload.js";
import WebcamIndicator from "./components/WebcamIndicator.js";
import { IoIosSend } from "react-icons/io";
import { FaMicrophone } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { MdMic, MdSearch } from "react-icons/md";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import EnvironmentCarousel from "./components/EnvironmentCarousel";
import Toast from "./components/Toast";
import VoiceCloning from "./components/VoiceCloning";
import ShareLinkCreator from "./components/ShareLinkCreator";
import MemoryChatView from "./components/MemoryChatView";
import ErrorBoundary from "./components/ErrorBoundary";
import AccountSettings from "./components/AccountSettings";
import Onboarding from "./components/Onboarding";
import ConversationManager from "./components/ConversationManager";
import DataExport from "./components/DataExport";
import LifeNarrative from "./components/LifeNarrative";
import AnalyticalPortrait from "./components/AnalyticalPortrait";
import TemporalEvolution from "./components/TemporalEvolution";
import FamilyTree from "./components/FamilyTree";
import CareDashboard from "./components/Care/CareDashboard";
import CrisisBanner from "./components/Care/CrisisBanner";
import CareIcon from "./components/Care/CareIcon";
import * as FaceRec from "./services/FaceRecognitionService";

const WS_RECONNECT_TIMEOUT = 1500

// Dynamic API URL configuration
const isProduction = process.env.NODE_ENV === 'production' || 
                    window.location.hostname !== 'localhost';

const api = isProduction 
  ? window.location.origin  // Use current domain in production (Railway)
  : "http://localhost:8000"; // Use localhost in development

console.log('API URL:', api, 'Environment:', process.env.NODE_ENV, 'Hostname:', window.location.hostname);

const IMAGE_ASSETS = [
  "warp-effect.png",
  "atlas-smile-purple-filter.png"
];
const VIDEO_ASSETS = [
  "voice_talk_example_trimmed_compressed_resized.mp4",
  "chat_examplee.mp4",
  "orb-graphic_compressed_resized.mp4",
  "Carl_jung_smoking_pipe_compressed_resized.mp4",
  "floating-psychological-self.mp4",
  "sphere-layers_compressed_resized.mp4"
];

function ModelLoader() {
  return (
    <div style={{
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0
    }}>
      <div style={{
        position: "relative",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        height: "100%",
        justifyContent: "center"
      }}>
        <LoadingDiv
            isLoading 
            duration={0.75} 
            width={`${32}px`}
            height={`${32}px`}
            borderWidth={2}
            loadingColor="#FFFFFF"
            borderColor="rgba(255, 255, 255, 0.5)"
            borderRadius={`${10}px`}
            backgroundColor="transparent"
            loadingSegmentPercentage={25}
        />
        <span style={{
          fontSize: 17,
          marginLeft: 15
        }}>Loading Graphics...</span>
      </div>
    </div>
  );
}

function BackgroundScene({ 
  isTalking, 
  assistantTalking, 
  visemeSequence, 
  currentEmote, 
  setCurrentEmote, 
  isIntroMode = false,
  onModelLoaded,
  isModelVisible=true,
  currentMicroExpression,
  setCurrentMicroExpression,
  customAvatarUrl,
  loadingPreferences,
  avatarRefreshKey,
  connectAnimationActive
}) {
  return (
    <Canvas
      style={{
        width: "100%",
        height: "100%",
        background: "transparent",
        zIndex: 0,
        pointerEvents: "auto",
      }}
      camera={{ 
        position: [0.09, 0.012, 0], // Adjust camera position for intro mode
        fov: 60, 
        near: 0.001 
      }}
      gl={{ alpha: true }}
    >
      <ambientLight color="#ffffff" intensity={isIntroMode ? 0.62 : 0.72} />
      <directionalLight color="#ffffff" position={[10, 7, 2]} intensity={isIntroMode ? 2.95 : 3.2} />
      <Suspense fallback={null}>
        {isModelVisible && !loadingPreferences && (
          <Model 
            key={avatarRefreshKey} // Force re-render when avatar changes
            isPlaying={isTalking}
            assistantTalking={assistantTalking}
            visemeSequence={visemeSequence}
            currentEmote={currentEmote}
            setCurrentEmote={setCurrentEmote}
            introPosition={isIntroMode}
            onLoad={onModelLoaded}
            connectAnimationActive={connectAnimationActive}
            currentMicroExpression={currentMicroExpression}
            setCurrentMicroExpression={setCurrentMicroExpression}
            avatarUrl={customAvatarUrl}
          />
        )}
        {isModelVisible && loadingPreferences && (
          <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[0.01, 8, 8]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={0.5} />
          </mesh>
        )}
        <OrbitControls
          enableZoom={true}
          enableRotate={true}
          enablePan={false}
        />
      </Suspense>
    </Canvas>
  );
}

function App() {
  const [modelLoaded, setModelLoaded] = useState(false);
  const [modelLoading, setModelLoading] = useState(true);

  const assetsLoaded = usePreloadAssets({
    images: IMAGE_ASSETS,
    videos: VIDEO_ASSETS
  });
  
  const handleModelLoaded = () => {
    setModelLoaded(true);
    setModelLoading(false);
  };

  // Audio context and oscillator refs
  const audioContextRef = useRef(null);
  const apiSelectedModel = useRef(null);

  const peerConnectionRef = useRef(null);
  const wsRef = useRef(null);
  // Guards the user_preferences 401 path so we only force a logout (hard page
  // reload) after a token refresh has already been tried once and still failed.
  const prefsAuthRetriedRef = useRef(false);
  const analyserRef = useRef(null);
  const convDetails = useRef(null);
  const localStreamRef = useRef(null); // Store local media stream for muting

  const assistantTalkingRef = useRef(null);

  // Initialize WebRTC connection
  const initiateWebRTC = async (withCamera = false) => {
    try {
      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: withCamera
      });

      // Store the local stream for muting control
      localStreamRef.current = localStream;

      if (withCamera) {
        const videoElement = document.getElementById('user-video');
        if (videoElement) {
          videoElement.srcObject = localStream;
        }
      }

      const iceServers = [
          { urls: "stun:stun.relay.metered.ca:80" },
      ];
      const turnUser = process.env.REACT_APP_TURN_SERVER_USERNAME;
      const turnCred = process.env.REACT_APP_TURN_SERVER_CREDENTIAL;
      if (turnUser && turnCred) {
        iceServers.push(
          { urls: "turn:global.relay.metered.ca:80", username: turnUser, credential: turnCred },
          { urls: "turn:global.relay.metered.ca:80?transport=tcp", username: turnUser, credential: turnCred },
          { urls: "turn:global.relay.metered.ca:443", username: turnUser, credential: turnCred },
          { urls: "turns:global.relay.metered.ca:443?transport=tcp", username: turnUser, credential: turnCred },
        );
      }
      peerConnectionRef.current = new RTCPeerConnection({ iceServers });

      localStream.getTracks().forEach(track => {
        peerConnectionRef.current.addTrack(track, localStream);
      });

      function removeRtxCodecs(sdp) {
        const lines = sdp.split('\r\n');
        const rtxPayloadTypes = [];
    
        // First, identify all RTX payload types
        lines.forEach(line => {
            if (line.includes('a=rtpmap') && line.includes('rtx/')) {
                const match = line.match(/a=rtpmap:(\d+) rtx\/\d+/);
                if (match && match[1]) {
                    rtxPayloadTypes.push(match[1]);
                }
            }
        });
    
        const filteredLines = lines.filter(line => {
            // Remove RTX related lines
            for (let payloadType of rtxPayloadTypes) {
                if (line.startsWith(`a=rtpmap:${payloadType}`) ||
                    line.startsWith(`a=fmtp:${payloadType}`) ||
                    line.startsWith(`a=rtcp-fb:${payloadType}`)) {
                    return false;
                }
            }
            return true;
        });
    
        // Now, remove RTX payload types from the m=video line
        const finalLines = filteredLines.map(line => {
            if (line.startsWith('m=video')) {
                const parts = line.split(' ');
                const mLine = parts.slice(0, 3); // "m=video", port, protocol
                const payloads = parts.slice(3).filter(pt => !rtxPayloadTypes.includes(pt));
                return [...mLine, ...payloads].join(' ');
            }
            return line;
        });
    
        return finalLines.join('\r\n');
    }

      peerConnectionRef.current.addTransceiver('audio', { direction: 'recvonly' });
      const offer = await peerConnectionRef.current.createOffer();
      offer.sdp = removeRtxCodecs(offer.sdp);
      await peerConnectionRef.current.setLocalDescription(offer);

      wsRef.current.send(
        JSON.stringify({
          type: "offer",
          sdp: offer.sdp,
          sessionId,
          token,
        })
      );

      peerConnectionRef.current.onicecandidate = (event) => {
        if (event.candidate && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "ice-candidate",
              candidate: event.candidate,
              sessionId
            })
          );
        }
      };

      peerConnectionRef.current.ontrack = (event) => {
        setPhoneCalling(false);
        setConversing(true);
        const audio = new Audio();
        audio.srcObject = event.streams[0];
        audio.muted = false;
        audio.volume = 1;
        audio.autoplay = true;
        document.body.appendChild(audio);
        audio.play().catch((e) => console.error("Audio play failed:", e));

        // Add Web Audio API for analysis only (no playback through it)
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            // Resume AudioContext if suspended (due to autoplay policies)
            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().then(() => {
                    console.log("AudioContext resumed");
                });
            }
        }
        const audioContext = audioContextRef.current;

        // Create a MediaStreamSource from the same stream
        const source = audioContext.createMediaStreamSource(event.streams[0]);

        // Set up AnalyserNode for silence detection
        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.fftSize = 2048;
        source.connect(analyserRef.current);

        let lastUpdateTime = 0;
        const debounceTime = 0; // 150ms to smooth out word-by-word toggling
        let silenceTimeout = null; // For delayed silence detection

        // Check audio activity
        const checkAudioActivity = (timestamp) => {
            const bufferLength = analyserRef.current.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            analyserRef.current.getByteTimeDomainData(dataArray);

            // Calculate RMS
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                const value = (dataArray[i] / 128) - 1; // Normalize to -1 to 1
                sum += value * value;
            }
            const rms = Math.sqrt(sum / bufferLength);

            const silenceThreshold = 0.02; // Tune this
            const isActive = rms > silenceThreshold;
            
            if (timestamp - lastUpdateTime >= debounceTime) {
              if (isActive) {
                  // Start talking immediately
                  setIsTalking(isActive)
                  // Clear any pending silence timeout
                  if (silenceTimeout) {
                      clearTimeout(silenceTimeout);
                      silenceTimeout = null;
                  }
              } else {
                  // Delay stopping to bridge short gaps
                  if (!silenceTimeout) {
                      silenceTimeout = setTimeout(() => {
                          setIsTalking(isActive);
                      }, 150);
                  }
              }
              lastUpdateTime = timestamp;
          }

            if (audioContext.state !== 'closed') {
              animationFrameIdRef.current = requestAnimationFrame(checkAudioActivity);
            }
        };

        // Start analysis
        animationFrameIdRef.current = requestAnimationFrame(checkAudioActivity);
      };
    } catch (error) {
      console.error("Error initiating WebRTC:", error);
    }
  };

  const handleClearMemories = () => {
    setMemories([]);
  };
  
  const handleClearChat = () => {
    setChat([]);
  };

  const handleDeleteMessage = async (message) => {
    // Update the chat state by removing the deleted message
    setChat(chat.filter(item => item.id !== message.id));
  };
  
  const handleDeleteMemory = async (memory) => {
    // Update the memories state by removing the deleted memory
    setMemories(memories.filter(m => 
      !(m.text === memory.text && m.category === memory.category)
    ));
  };

  const handleSaveMemory = async () => {
    try {
      if (!sessionId || !token) {
        console.error("No session ID or token available for memory save");
        return;
      }
      
      const response = await fetch(`${api}/save_memory_now`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log("✅ Memory saved successfully:", result.message);
        
        // Refresh memories to show the new one
        await fetchMemories();
        
        // Show success toast
        showToast("Memory saved", "success");
      } else {
        const error = await response.json();
        console.error("❌ Error saving memory:", error.detail);
        showToast("Failed to save memory", "error");
      }
    } catch (error) {
      console.error("❌ Error saving memory:", error);
      showToast("Error saving memory", "error");
    }
  };

  const handleToggleVision = async (enabled) => {
    setIsVisionEnabled(enabled);
    
    // Send vision toggle to backend
    try {
      await fetch(`${api}/toggle_vision_capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          vision_enabled: enabled
        })
      });
    } catch (error) {
      console.error("Error toggling vision:", error);
    }
  };

  const { token, user, loading, signInWithGoogle, logout, error, deviceIssues } = useAuth();
  // Care dashboard: the managed account whose dashboard is open (null = closed),
  // and which tab to land on. The heart nav icon and the CrisisBanner set these.
  // NOTE: caregiver "view memories as them" delegation is intentionally NOT
  // ported in this stage — no actingAsUser / ?as_user_id wiring here.
  const [careDashAccount, setCareDashAccount] = useState(null);
  const [careDashTab, setCareDashTab] = useState('overview');
  const [isTalking, setIsTalking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [memories, setMemories] = useState([]);
  const memoriesRef = useRef([]);
  const [showMemoryManager, setShowMemoryManager] = useState(false);
  const [conversing, setConversing] = useState(false);
  const [calling, setPhoneCalling] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [connectAnimationActive, setConnectAnimationActive] = useState(false);
  const [chat, setChat] = useState([])
  const [loadingChat, setLoadingChat] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false);
  const animationFrameIdRef = useRef(null);
  const [isWsOpen, toggleWsOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(true);
  const [currentEmote, setCurrentEmote] = useState(null);
  const [currentMicroExpression, setCurrentMicroExpression] = useState(null);

  const [showModelSelectionScreen, setShowModelSelectionScreen] = useState(false);
  const [showSoulOrb, setShowSoulOrb] = useState(false);
  const [showPrivacyPanel, setShowPrivacyPanel] = useState(false);
  const [showWisdomArchive, setShowWisdomArchive] = useState(false);
  const [highlightedMemories, setHighlightedMemories] = useState([]);
  const [appView, setAppViewRaw] = useState(() => {
    const saved = localStorage.getItem('appView');
    return (saved === 'avatar' || saved === 'memory-chat') ? saved : 'memory-chat';
  }); // 'memory-chat' | 'avatar'
  const setAppView = (view) => {
    setAppViewRaw(view);
    localStorage.setItem('appView', view);
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;
  const isSmallSize = windowWidth < 1200;
  const smallerThan850 = windowWidth < 850;
  const [isChatExpanded, toggleExpandChat] = useState(!isMobile)
  const [isMemoryExpanded, toggleMemoryExpanded] = useState(false)
  // Windowed memories list — mounting 1000+ MemoryCards at once froze the
  // panel. Render in pages of 60; "Show more" extends the window.
  const MEMORY_PAGE = 60
  const [visibleMemoryCount, setVisibleMemoryCount] = useState(MEMORY_PAGE)
  useEffect(() => { setVisibleMemoryCount(MEMORY_PAGE) }, [memories.length])

  const [isMemoryEnabled, setIsMemoryEnabled] = useState(true);
  const [isChatEnabled, setIsChatEnabled] = useState(true);
  const [aiAutoCallsEnabled, setAiAutoCallsEnabled] = useState(true);
  const [isTogglingMemory, setIsTogglingMemory] = useState(false);
  const [isTogglingChat, setIsTogglingChat] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  
  // Custom Avatar states
  const [customAvatarUrl, setCustomAvatarUrl] = useState("");
  const [customAvatarInput, setCustomAvatarInput] = useState("");
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);
  const [avatarSuccess, setAvatarSuccess] = useState("");

  // Custom Voice states
  const [customVoiceId, setCustomVoiceId] = useState("");
  const [customVoiceInput, setCustomVoiceInput] = useState("");
  const [isUpdatingVoice, setIsUpdatingVoice] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  // TTS provider preference: 'dxtlabs' (default), 'elevenlabs', or 'openai'.
  // Note: a custom voice ID forces 'elevenlabs' server-side regardless of this,
  // since dxtlabs/openai can't consume an ElevenLabs voice ID.
  const [ttsProvider, setTtsProvider] = useState("dxtlabs");
  const [isUpdatingTtsProvider, setIsUpdatingTtsProvider] = useState(false);
  
  // Memory Owner states
  const [memoryOwnerName, setMemoryOwnerName] = useState("");
  const [memoryOwnerDescription, setMemoryOwnerDescription] = useState("");
  // User's city — geocoded server-side for the right local time + weather context
  const [userLocation, setUserLocation] = useState("");
  const [isUpdatingMemoryOwner, setIsUpdatingMemoryOwner] = useState(false);
  const [memoryOwnerError, setMemoryOwnerError] = useState("");
  const [memoryOwnerSuccess, setMemoryOwnerSuccess] = useState("");
  const [voiceSuccess, setVoiceSuccess] = useState("");
  const [userVoiceMessage, setUserVoiceMessage] = useState(null);
  // const [assistantMessage, setAssistantVoiceMessage] = useState("I can see from your facial expressions and your voice that you are in a good mood! I think it would be perfect for us to explore ");
  const [assistantMessage, setAssistantVoiceMessage] = useState(null);
  const [latestMessageType, setLatestMessageType] = useState(null);

  const [userMessageKey, setUserMessageKey] = useState(0);
  const [assistantMessageKey, setAssistantMessageKey] = useState(0);
  const [isCallDropdownVisible, setIsCallDropdownVisible] = useState(false);
  const callDropdownRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [assistantTalking, setAssistantTalking] = useState(false);
  const [visemes, setVisemes] = useState();

  // Vision-related state
  const [isVisionEnabled, setIsVisionEnabled] = useState(true);  // Enable by default
  const [visionAnalysis, setVisionAnalysis] = useState(null); // No popups, always null
  const [lastVisionAnalysis, setLastVisionAnalysis] = useState(null);
  
  // Toast notification state
  const [toastMessage, setToastMessage] = useState(null);

  // Auto-speak: AI reads responses aloud
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem('autoSpeak') === 'true');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakVolume, setSpeakVolume] = useState(() => parseFloat(localStorage.getItem('speakVolume') || '0.8'));
  const [speakSpeed, setSpeakSpeed] = useState(() => parseFloat(localStorage.getItem('speakSpeed') || '1'));
  const autoSpeakRef = useRef(localStorage.getItem('autoSpeak') === 'true');
  const speakVolumeRef = useRef(parseFloat(localStorage.getItem('speakVolume') || '0.8'));
  const speakSpeedRef = useRef(parseFloat(localStorage.getItem('speakSpeed') || '1'));
  const currentAudioRef = useRef(null);
  const tokenRef = useRef(null);
  const appViewRef = useRef('memory-chat');

  // Keep refs in sync with state so WebSocket handler always sees latest values
  useEffect(() => { appViewRef.current = appView; }, [appView]);
  useEffect(() => { autoSpeakRef.current = autoSpeak; localStorage.setItem('autoSpeak', autoSpeak); }, [autoSpeak]);
  useEffect(() => { speakVolumeRef.current = speakVolume; localStorage.setItem('speakVolume', String(speakVolume)); }, [speakVolume]);
  useEffect(() => { speakSpeedRef.current = speakSpeed; localStorage.setItem('speakSpeed', String(speakSpeed)); }, [speakSpeed]);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Stop speaking helper
  const stopSpeaking = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // Keyboard shortcut: Ctrl+M to toggle auto-speak
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'm') {
        e.preventDefault();
        setAutoSpeak(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Proactive Mode ──
  const [proactiveMode, setProactiveMode] = useState(() => localStorage.getItem('proactiveMode') === 'true');
  const proactiveModeRef = useRef(localStorage.getItem('proactiveMode') === 'true');
  const lastInteractionRef = useRef(Date.now());
  const proactiveCountTodayRef = useRef(0);
  const proactiveDateRef = useRef(new Date().toDateString());
  const isUserVisibleRef = useRef(true);
  const isPersonDetectedRef = useRef(false);
  const proactiveTimerRef = useRef(null);
  const [proactiveStream, setProactiveStream] = useState(null);
  const presenceVideoRef = useRef(null);
  const presenceIntervalRef = useRef(null);

  // Face recognition state
  const [faceStatus, setFaceStatus] = useState('no-one'); // 'no-one' | 'detected' | 'recognized' | 'stranger'
  const faceStatusRef = useRef('no-one');
  const [hasEnrolledFace, setHasEnrolledFace] = useState(() => FaceRec.hasEnrolledFace());
  const prevFaceStatusRef = useRef('no-one');
  const ownerLastSeenRef = useRef(null);      // timestamp when owner was last seen
  const ownerLastGreetedRef = useRef(null);    // timestamp of last face-triggered greeting
  const ownerGreetedSessionRef = useRef(false); // whether we've greeted this session

  useEffect(() => {
    proactiveModeRef.current = proactiveMode;
    localStorage.setItem('proactiveMode', proactiveMode);
  }, [proactiveMode]);

  // Smart "owner arrival" trigger — greets naturally, not on every flicker
  useEffect(() => {
    const prev = prevFaceStatusRef.current;
    prevFaceStatusRef.current = faceStatus;

    // Track when owner was last seen (for calculating absence duration)
    if (faceStatus === 'recognized') {
      ownerLastSeenRef.current = Date.now();
    }

    // Only act on transitions TO recognized
    if (faceStatus !== 'recognized' || prev === 'recognized') return;
    if (!proactiveModeRef.current || wsRef.current?.readyState !== WebSocket.OPEN) return;

    const now = Date.now();
    const hour = new Date().getHours();
    if (hour < 8 || hour > 22) return; // quiet hours

    const neverGreeted = !ownerGreetedSessionRef.current;
    const lastGreeted = ownerLastGreetedRef.current;
    const lastSeen = ownerLastSeenRef.current;
    const minutesSinceGreeted = lastGreeted ? (now - lastGreeted) / 60000 : Infinity;
    const minutesAbsent = lastSeen ? (now - lastSeen) / 60000 : Infinity;

    // Greet if: (a) first time this session, OR (b) owner was absent 30+ min and hasn't been greeted in 30+ min
    const shouldGreet = neverGreeted || (minutesAbsent >= 30 && minutesSinceGreeted >= 30);

    if (shouldGreet) {
      const hint = neverGreeted
        ? 'webcam (owner just sat down for the first time — welcome them warmly)'
        : 'webcam (owner returned after being away for a while — welcome them back naturally)';

      console.log(`[FaceRec] Owner arrived (first: ${neverGreeted}, absent: ${Math.round(minutesAbsent)}min) — greeting`);
      wsRef.current.send(JSON.stringify({
        type: 'PROACTIVE_CHECK',
        face_status: 'recognized',
        presence_hint: hint
      }));
      ownerGreetedSessionRef.current = true;
      ownerLastGreetedRef.current = now;
      lastInteractionRef.current = now;
    }
  }, [faceStatus]);

  // Track page visibility + refresh memories when tab regains focus
  useEffect(() => {
    const handleVisibility = () => {
      isUserVisibleRef.current = !document.hidden;
      // Refresh memories when user returns to tab (picks up mobile captures)
      if (!document.hidden && token) {
        fetchMemories();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token]);

  // Track last user interaction (typing, clicking, sending message)
  useEffect(() => {
    const markActive = () => { lastInteractionRef.current = Date.now(); };
    window.addEventListener('click', markActive);
    window.addEventListener('keydown', markActive);
    return () => {
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
    };
  }, []);

  // Webcam-based presence detection with face-api.js recognition
  useEffect(() => {
    if (!proactiveMode) {
      // Cleanup when proactive mode is off
      if (presenceIntervalRef.current) clearInterval(presenceIntervalRef.current);
      if (presenceVideoRef.current?.srcObject) {
        presenceVideoRef.current.srcObject.getTracks().forEach(t => t.stop());
        presenceVideoRef.current.srcObject = null;
      }
      isPersonDetectedRef.current = false;
      setProactiveStream(null);
      setFaceStatus('no-one');
      faceStatusRef.current = 'no-one';
      return;
    }

    let video = null;

    const initPresenceDetection = async () => {
      try {
        // Load face-api.js models
        await FaceRec.loadModels();

        // Create hidden video element for presence detection
        video = document.createElement('video');
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.style.display = 'none';
        document.body.appendChild(video);
        presenceVideoRef.current = video;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240, facingMode: 'user' },
          audio: false
        });
        video.srcObject = stream;
        setProactiveStream(stream);
        await video.play();

        // Run face recognition every 10 seconds
        presenceIntervalRef.current = setInterval(async () => {
          if (!proactiveModeRef.current || !video.srcObject) return;
          try {
            const result = await FaceRec.recognizeFromVideo(video);
            const newStatus = result.status;
            faceStatusRef.current = newStatus;
            setFaceStatus(newStatus);
            isPersonDetectedRef.current = newStatus !== 'no-one';
            console.log(`[FaceRec] Status: ${newStatus}, faces: ${result.faceCount}${result.distance !== null ? `, dist: ${result.distance.toFixed(3)}` : ''}`);
          } catch (e) {
            console.warn('[FaceRec] Detection error:', e);
            isPersonDetectedRef.current = isUserVisibleRef.current;
          }
        }, 10000);

      } catch (e) {
        console.warn('Proactive: webcam access denied, using visibility only', e);
        isPersonDetectedRef.current = true;
      }
    };

    initPresenceDetection();

    return () => {
      if (presenceIntervalRef.current) clearInterval(presenceIntervalRef.current);
      if (video?.srcObject) {
        video.srcObject.getTracks().forEach(t => t.stop());
        video.srcObject = null;
      }
      if (video?.parentNode) video.parentNode.removeChild(video);
    };
  }, [proactiveMode]);

  // Proactive timer: checks conditions and fires PROACTIVE_CHECK via WebSocket
  useEffect(() => {
    if (!proactiveMode) {
      if (proactiveTimerRef.current) clearInterval(proactiveTimerRef.current);
      return;
    }

    const getRandomInterval = () => {
      // 20-45 minutes in milliseconds
      return (20 + Math.random() * 25) * 60 * 1000;
    };

    const scheduleNext = () => {
      if (proactiveTimerRef.current) clearTimeout(proactiveTimerRef.current);
      const interval = getRandomInterval();
      proactiveTimerRef.current = setTimeout(() => {
        tryProactiveMessage();
        scheduleNext();
      }, interval);
    };

    const tryProactiveMessage = () => {
      // Reset daily counter
      const today = new Date().toDateString();
      if (proactiveDateRef.current !== today) {
        proactiveDateRef.current = today;
        proactiveCountTodayRef.current = 0;
      }

      const hour = new Date().getHours();
      const minutesSinceInteraction = (Date.now() - lastInteractionRef.current) / 60000;
      const isPresent = isUserVisibleRef.current || isPersonDetectedRef.current;
      const currentFaceStatus = faceStatusRef.current;

      // Privacy guard: don't send proactive messages when a stranger is detected
      if (currentFaceStatus === 'stranger') {
        console.log('[FaceRec] Stranger detected — skipping proactive message for privacy');
        return;
      }

      // Conditions to send proactive message:
      // 1. Proactive mode is on
      // 2. User is present (page visible OR webcam detects person)
      // 3. At least 15 min since last interaction (cooldown)
      // 4. Between 8am-10pm (reasonable hours)
      // 5. Max 5 proactive messages per day
      // 6. WebSocket is connected
      // 7. No stranger is present (privacy guard — checked above)
      if (
        proactiveModeRef.current &&
        isPresent &&
        minutesSinceInteraction >= 15 &&
        hour >= 8 && hour <= 22 &&
        proactiveCountTodayRef.current < 5 &&
        wsRef.current?.readyState === WebSocket.OPEN
      ) {
        const fs = faceStatusRef.current;
        const presenceHint = fs === 'recognized' ? 'webcam (owner recognized)'
          : fs === 'stranger' ? 'webcam (stranger detected)'
          : fs === 'detected' ? 'webcam (face detected, not enrolled)'
          : isPersonDetectedRef.current ? 'webcam (person detected)'
          : 'page visibility';
        wsRef.current.send(JSON.stringify({
          type: 'PROACTIVE_CHECK',
          face_status: fs,
          presence_hint: presenceHint
        }));
        proactiveCountTodayRef.current++;
        lastInteractionRef.current = Date.now(); // Reset cooldown
      }
    };

    scheduleNext();

    return () => {
      if (proactiveTimerRef.current) clearTimeout(proactiveTimerRef.current);
    };
  }, [proactiveMode]);

  const modelDropdownRef = useRef(null);
  const [isModelVisible, setIsModelVisible] = useState(true);
  const [areEnvsLoaded, setAreEnvsLoaded] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState(() => {
    const savedEnv = localStorage.getItem("selectedEnv");
    if (savedEnv) {
      return JSON.parse(savedEnv);
    }
    return { name: "Cabinet", url: "default-env-cabinet.jpg" };
  });
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const autoScrollRef = useRef(null);

  const [environments, setEnvironments] = useState([
    {
      name: "Cabinet",
      fullImage: "default-env-cabinet.jpg",
      thumbnail: "default-env-cabinet.jpg"
    },
    {
      name: "Black",
      color: "#000000"
    },
    {
      name: "Green",
      color: "#1a3a1a"
    },
    {
      name: "White",
      color: "#ffffff"
    }
  ]);

  const INTRO_MODE = "INTRO_MODE"
  const APP_MODE = "APP_MODE"
  const [mode, setMode] = useState(null);
  const showIntroMode = mode === INTRO_MODE
  const [showLoginView, setShowLoginView] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAvatarSettings, setShowAvatarSettings] = useState(false);
  const [avatarEnrolling, setAvatarEnrolling] = useState(false);
  const [avatarEnrollProgress, setAvatarEnrollProgress] = useState('');
  const avatarSettingsBtnRef = useRef(null);
  const avatarSettingsPopoverRef = useRef(null);
  // Close avatar settings popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inBtn = avatarSettingsBtnRef.current && avatarSettingsBtnRef.current.contains(e.target);
      const inPopover = avatarSettingsPopoverRef.current && avatarSettingsPopoverRef.current.contains(e.target);
      if (!inBtn && !inPopover) {
        setShowAvatarSettings(false);
      }
    };
    if (showAvatarSettings) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAvatarSettings]);
  const [showEnvironmentModal, setShowEnvironmentModal] = useState(false);
  const [showVoiceCloningModal, setShowVoiceCloningModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showConversationManager, setShowConversationManager] = useState(false);
  const [showDataExport, setShowDataExport] = useState(false);
  const [showLifeNarrative, setShowLifeNarrative] = useState(false);
  const [showAnalyticalPortrait, setShowAnalyticalPortrait] = useState(false);
  const [showTemporalEvolution, setShowTemporalEvolution] = useState(false);
  const [showFamilyTree, setShowFamilyTree] = useState(false);

  const [showTipCard, setShowTipCard] = useState(false); // Always disabled
  const progressIntervalRef = useRef(null);
  const tipShownRef = useRef(false);

  const [isFirefox, setIsFirefox] = useState(false);
  const [showFirefoxTooltip, setShowFirefoxTooltip] = useState(false);

  const [isTranscriptionExpanded, setExpandTranscription] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [conversationToSave, setConversationToSave] = useState(null);

  const fullEmoteRef = useRef(null)
  const microEmoteRef = useRef(null)
  const sessionIdRef = useRef(null)
  const publicWsConnectingRef = useRef(false)

  const [isUpdatingModel, setIsUpdatingModel] = useState(false);

  // Share link state
  const [shareLinks, setShareLinks] = useState([]);
  const [loadingShareLinks, setLoadingShareLinks] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [showShareLinkCreator, setShowShareLinkCreator] = useState(false);

  // Public share mode state
  const [isPublicMode, setIsPublicMode] = useState(false);
  const [publicShareId, setPublicShareId] = useState(null);

  useEffect(() => {
    sessionIdRef.current = sessionId
    // For public mode, set chat loading to false immediately
    if (isPublicMode && sessionId) {
      setChatLoading(false);
    }
  }, [sessionId, isPublicMode])

  // Initialize public share mode from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareId = urlParams.get('shareId');
    const sessionIdFromUrl = urlParams.get('sessionId');
    const isPublic = urlParams.get('isPublic') === 'true';
    
    if (shareId && sessionIdFromUrl && isPublic) {
      setIsPublicMode(true);
      setPublicShareId(shareId);
      setSessionId(sessionIdFromUrl);
      setSelectedModel("Echo"); // Public shares are always Echo mode
      setMode(APP_MODE); // Bypass intro screen for public shares
      setAreEnvsLoaded(true); // Mark environments as loaded for public mode
      // Set default environment for public mode
      setSelectedEnv({ name: 'Cabinet' });
      
      // Load shared preferences from sessionStorage
      const sharedPrefsStr = sessionStorage.getItem('publicSharePreferences');
      if (sharedPrefsStr) {
        try {
          const sharedPrefs = JSON.parse(sharedPrefsStr);
          setCustomAvatarUrl(sharedPrefs.custom_avatar_url || "");
          setCustomAvatarInput(sharedPrefs.custom_avatar_url || "");
          setCustomVoiceId(sharedPrefs.custom_voice_id || "");
          setCustomVoiceInput(sharedPrefs.custom_voice_id || "");
          setMemoryOwnerName(sharedPrefs.memory_owner_name || "");
          setMemoryOwnerDescription(sharedPrefs.memory_owner_description || "");
          // Set defaults for public mode
          setIsMemoryEnabled(true); // Enable memories in public mode for Echo
          setIsChatEnabled(true); // Chat is enabled
          setLoadingPreferences(false);
          // Clean up after reading
          sessionStorage.removeItem('publicSharePreferences');
        } catch (e) {
          console.error("Error loading shared preferences:", e);
          setLoadingPreferences(false);
        }
      } else {
        setLoadingPreferences(false);
      }
      
      // For public shares, keep the share URL visible
      const newUrl = window.location.origin + `/share/${shareId}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Auto-connect WebSocket for public shares - only run once
  useEffect(() => {
    if (isPublicMode && sessionId && !publicWsConnectingRef.current) {
      // Check if WebSocket is already connected or connecting
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        return; // Already connected
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
        return; // Already connecting
      }
      
      console.log("🔗 Auto-connecting WebSocket for public share session:", sessionId);
      publicWsConnectingRef.current = true;
      (async () => {
        await createAndConnectWs(sessionId, null, true); // No token needed for public shares
      })();
    }
  }, [isPublicMode, sessionId]); // Removed isWsOpen from dependencies

  const updateModel = async (model) => {
    setIsUpdatingModel(true);
    try {
      const res = await fetch(`${api}/update_model_selection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ modelSelection: model, sessionId: sessionIdRef.current })
      });

      if (!res.ok) throw new Error("Failed to update model");
      
      setSelectedModel(model);
      setShowModelSelectionScreen(false);
      showToast(`Model updated to ${model}`, "success");

      if (!apiSelectedModel.current) {
        apiSelectedModel.current = model;
        (async () => {
          await createAndConnectWs(sessionId, token, false);
        })();
      } else {
        apiSelectedModel.current = model;
      }
    } catch (err) {
      console.error("Error updating model:", err);
      showToast("Failed to update model", "error");
    } finally {
      setIsUpdatingModel(false);
    }
  };

  useEffect(() => {
    const isFirefoxBrowser = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    setIsFirefox(isFirefoxBrowser);
  }, []);;

  useEffect(() => {
    // Disable tip card popup
    // if (!showIntroMode && !token && !conversing && !tipShownRef.current && !loading) {
    //   setShowTipCard(true);
    //   tipShownRef.current = true;
    // }
  }, [showIntroMode, token, conversing, loading]);

  useEffect(() => {
    if (showTipCard) {
      const startTime = Date.now();
      const duration = 20000;

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      progressIntervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const progressPercent = (elapsedTime / duration) * 100;

        const progressBar = document.getElementById('tip-progress-bar');
        if (progressBar) {
          progressBar.style.width = `${Math.min(progressPercent, 100)}%`;
        }

        if (elapsedTime >= duration) {
          clearInterval(progressIntervalRef.current);
          setShowTipCard(false);
        }
      }, 100);

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  }, [showTipCard]);

  const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] = useState(false);

  const handleStartApp = () => {
    setMode(APP_MODE)
  };

  useEffect(() => {
    assistantTalkingRef.current = assistantTalking
  }, [assistantTalking])

  // Auto-mute microphone during voice conversations when assistant is talking
  useEffect(() => {
    if (conversing && localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      
      audioTracks.forEach(track => {
        if (assistantTalking) {
          console.log("🔇 Muting microphone: Assistant is speaking");
          track.enabled = false; // Mute the microphone
        } else {
          console.log("🎤 Unmuting microphone: Assistant finished speaking");
          track.enabled = true; // Unmute the microphone
        }
      });
    }
  }, [assistantTalking, conversing]);

  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showAvatarBackground, setShowAvatarBackground] = useState(() => {
    const saved = localStorage.getItem('showAvatarBackground');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    if (selectedEnv) {
      if (!showAvatarBackground) {
        document.body.style.backgroundImage = 'none';
        document.body.style.backgroundColor = '#302b23';
      } else if (selectedEnv.name === 'Cabinet') {
        document.body.style.backgroundImage = `url('${env1Url}')`;
        document.body.style.backgroundColor = '';
      } else if (selectedEnv.color) {
        document.body.style.backgroundImage = 'none';
        document.body.style.backgroundColor = selectedEnv.color;
      } else {
        document.body.style.backgroundImage = `url('${selectedEnv.url}')`;
        document.body.style.backgroundColor = '';
      }
    }
  }, [selectedEnv, showAvatarBackground]);

    // Add this with your other useEffect hooks
  useEffect(() => {
    // Function to handle clicks outside the dropdown
    function handleClickOutside(event) {
      if (modelDropdownRef.current && 
          !modelDropdownRef.current.contains(event.target) && 
          isModelDropdownOpen) {
        setIsModelDropdownOpen(false);
      }
    }
    
    // Add event listener when dropdown is open
    if (isModelDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModelDropdownOpen]); // Re-run effect when dropdown state changes

  useEffect(() => {
    // Function to handle clicks outside the dropdown
    function handleClickOutside(event) {
      if (modelDropdownRef.current && 
          !modelDropdownRef.current.contains(event.target) && 
          isModelDropdownOpen) {
        setIsModelDropdownOpen(false);
      }
    }
    
    // Add event listener when dropdown is open
    if (isModelDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModelDropdownOpen]); // Re-run effect when dropdown state changes

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if dropdown is open and click is outside dropdown and button
      if (isCallDropdownVisible && 
          callDropdownRef.current && 
          !callDropdownRef.current.contains(event.target) &&
          !event.target.closest('.lets-connect-button')) {
        setIsCallDropdownVisible(false);
      }
    };
    
    // Add event listener when dropdown is open
    if (isCallDropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCallDropdownVisible]);

  useEffect(() => {
    if (areEnvsLoaded) {
      const savedEnv = window.localStorage.getItem('selectedEnvironment');
      if (savedEnv && environments.find(env => env.name === savedEnv)) {
        const env = environments.find(env => env.name === savedEnv)
        setSelectedEnv({
          name: env.name,
          url: env.fullImage,
          color: env.color
        })
      } else {
        setSelectedEnv({
          name: "Cabinet"
        })
      }
    }
  }, [environments, areEnvsLoaded])

  const envUserChangedRef = useRef(false);
  useEffect(() => {
    if (selectedEnv) {
      window.localStorage.setItem('selectedEnvironment', selectedEnv.name);
      // Only show toast when user explicitly changes environment
      if (envUserChangedRef.current) {
        showToast(`Environment changed to ${selectedEnv.name}`, "success");
        envUserChangedRef.current = false;
      }
    }
  }, [selectedEnv])

  const prevUserMessageRef = useRef(null);
  const prevAssistantMessageRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const manualCloseRef = useRef(false);

  const handlePlayMessageEmote = (emoteType) => {
    if (emoteType) {
      setCurrentEmote(emoteType);
    }
  };

  const processPhoneCallEvents = (messages) => {
    const processed = [];
    let i = 0;
  
    while (i < messages.length) {
      // Check if the current message is a "Phone call started" event
      if (messages[i].type === "CONVERSATION_EVENT" && messages[i].content === "Phone call started") {
        let j = i + 1;
  
        // Look for the next "Phone call ended" event
        while (j < messages.length && !(messages[j].type === "CONVERSATION_EVENT" && messages[j].content === "Phone call ended")) {
          j++;
        }
  
        if (j < messages.length) {
          // Found a matching "Phone call ended" event
          const startTime = messages[i].timestamp;
          const endTime = messages[j].timestamp;
          const duration = endTime - startTime;
  
          // Create a new event with start timestamp and duration
          processed.push({
            id: messages[i].id,
            type: "CONVERSATION_EVENT",
            content: `Phone call duration: ${duration} seconds`,
            timestamp: startTime,
            duration: formatDuration(duration)
          });
  
          // Skip past the "Phone call ended" event
          i = j + 1;
        } else {
          // No "Phone call ended" found, keep the "Phone call started" event as is
          processed.push(messages[i]);
          i++;
        }
      } else {
        // Not a "Phone call started" event, add the message as is
        processed.push(messages[i]);
        i++;
      }
    }
  
    return processed;
  };

  // Add this function to the App component
  const fetchConversationHistory = async () => {
    if (!token || !user) return;
  
    try {
      const res = await fetch(`${api}/user_conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
  
      // Extract messages from the response
      const messages = data.messages || [];

      const processedMessages = processPhoneCallEvents(messages);
  
      // Process messages to add date separators
      const allMessages = [];
      let currentDate = null;
  
      processedMessages.forEach(message => {
        const messageDate = new Date(message.timestamp * 1000);
        const year = messageDate.getFullYear();
        const month = (messageDate.getMonth() + 1).toString().padStart(2, '0');
        const day = messageDate.getDate().toString().padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
  
        // If the date has changed (or it's the first message), add a date separator
        if (dateStr !== currentDate) {
          currentDate = dateStr;
          allMessages.push({
            type: 'DATE_SEPARATOR',
            content: formatDateSeparator(messageDate), // e.g., "2025-02-01"
            timestamp: message.timestamp // Use the timestamp of the first message of this day
          });
        }
  
        // Add the original message
        allMessages.push(message);
      });
  

      setChatLoading(false);
      // Set the chat with the processed messages, even if empty
      setChat(allMessages);
    } catch (error) {
      console.error("Error fetching conversation history:", error);
    }
  };

  const fetchMemories = async () => {
    try {
      const res = await fetch(api + "/retrieve_memories", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const fetched = data.memories || [];

      // Normalize relative media URLs so they resolve against the backend
      const normalizeUrl = (url) => {
        if (url && url.startsWith('/media/')) return api + url;
        return url;
      };
      const normalized = fetched.map(m => ({
        ...m,
        media_url: normalizeUrl(m.media_url),
        thumbnail_url: normalizeUrl(m.thumbnail_url),
        media_items: (m.media_items || []).map(item => ({
          ...item,
          media_url: normalizeUrl(item.media_url),
          thumbnail_url: normalizeUrl(item.thumbnail_url),
        })),
      }));

      memoriesRef.current = normalized;
      setMemories(normalized);
      return normalized;
    } catch (error) {
      console.error("Error fetching memories:", error);
      return [];
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createAndConnectWs = async (currentSessionId, currentToken, isPublic = false) => {
    // Prevent multiple connections
    if (wsRef.current && 
        (wsRef.current.readyState === WebSocket.OPEN || 
         wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log("🔗 WebSocket already connected or connecting, skipping");
      return;
    }
    
    // Refresh token before connecting (to handle token expiration)
    let freshToken = currentToken;
    if (!isPublic && user) {
      try {
        const { refreshAuthToken } = await import('./auth/firebase');
        const newToken = await refreshAuthToken();
        if (newToken) freshToken = newToken;
        console.log("🔑 Token refreshed before WebSocket connection");
      } catch (error) {
        console.error("❌ Failed to refresh token:", error);
        // Fall back to current token if refresh fails
      }
    }
    
    const wsUrlPath = freshToken ? `/ws?token=${freshToken}&session_id=${currentSessionId}` : '/ws?session_id=' + currentSessionId
    const wsUrl = api.replace("https", "wss").replace("http", "ws") + wsUrlPath;
    console.log("🔗 Creating WebSocket connection to:", wsUrl);
    wsRef.current = new WebSocket(wsUrl);

    // 5) When we get the answer, set it as remote description
    wsRef.current.onmessage = async (event) => {
      const message = JSON.parse(event.data);
      if (message.type === "answer") {
        await peerConnectionRef.current.setRemoteDescription(message)
      } else if (message.type === "ice-candidate") {
        await peerConnectionRef.current.addIceCandidate(
          new RTCIceCandidate(message.candidate)
        );
      } else if (message.type === "PROCESSING") {
        setProcessing(true);
      } else if (message.type === "FINISHED_PROCESSING") {
        setProcessing(false);
      } else if (message.type === "CHAT_MESSAGE") {
        setChat(chat => {
          // Check if we need to add a Today separator
          const updatedChat = [...chat];
          const currentTimestamp = Math.floor(Date.now() / 1000); // Current time in seconds
          
          // If we need a "Today" separator, add it
          if (needsTodaySeparator(updatedChat)) {
            updatedChat.push({
              type: 'DATE_SEPARATOR',
              content: 'Today',
              timestamp: currentTimestamp
            });
          }
          
          // Add the new message
          updatedChat.push({
            "role": "assistant",
            "content": message.message,
            "emote_type": message.emote_type,
            "timestamp": currentTimestamp,
            "referenced_memories": message.referenced_memories || []
          });
          
          return updatedChat;
        });
        // Update highlighted memories for the Memory Chat graph
        if (message.referenced_memories && message.referenced_memories.length > 0) {
          setHighlightedMemories(message.referenced_memories);
        }
        setLoadingChat(false)
        // Auto-speak the assistant's response if enabled (skip in avatar mode — avatar has its own WebRTC voice).
        // Also fires for AI-initiated greetings sent on WebSocket connect (backend uses CHAT_MESSAGE
        // type for those too). The browser will block the first play() if no user gesture has
        // happened yet, which is the correct platform behavior.
        if (autoSpeakRef.current && message.message && tokenRef.current && appViewRef.current !== 'avatar') {
          const ttsText = message.message.substring(0, 4000);
          if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
          }
          setIsSpeaking(false);
          fetch(
            `${api}/api/memories/text-to-speech?text=${encodeURIComponent(ttsText)}&skip_transform=true`,
            { method: 'POST', headers: { Authorization: `Bearer ${tokenRef.current}` } }
          )
            .then(res => res.ok ? res.json() : Promise.reject(res.status))
            .then(data => {
              if (data.audio_data) {
                const audio = new Audio(data.audio_data);
                audio.volume = speakVolumeRef.current;
                audio.playbackRate = speakSpeedRef.current;
                currentAudioRef.current = audio;
                audio.onplay = () => setIsSpeaking(true);
                audio.onended = () => setIsSpeaking(false);
                audio.onpause = () => setIsSpeaking(false);
                audio.play().catch(e => console.warn('Audio play blocked:', e));
              }
            })
            .catch(err => {
              console.error('Auto-speak TTS error:', err);
              setIsSpeaking(false);
            });
        }
      } else if (message.type === "PROACTIVE_MESSAGE") {
        // Proactive mode: AI initiated this message
        setChat(chat => {
          const updatedChat = [...chat];
          const currentTimestamp = Math.floor(Date.now() / 1000);
          if (needsTodaySeparator(updatedChat)) {
            updatedChat.push({ type: 'DATE_SEPARATOR', content: 'Today', timestamp: currentTimestamp });
          }
          updatedChat.push({
            "role": "assistant",
            "content": message.message,
            "timestamp": currentTimestamp,
            "isProactive": true
          });
          return updatedChat;
        });
        // Auto-speak proactive messages if enabled (skip in avatar mode — avatar has its own WebRTC voice)
        if (autoSpeakRef.current && message.message && tokenRef.current && appViewRef.current !== 'avatar') {
          const ttsText = message.message.substring(0, 4000);
          if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
          }
          setIsSpeaking(false);
          fetch(
            `${api}/api/memories/text-to-speech?text=${encodeURIComponent(ttsText)}&skip_transform=true`,
            { method: 'POST', headers: { Authorization: `Bearer ${tokenRef.current}` } }
          )
            .then(res => res.ok ? res.json() : Promise.reject(res.status))
            .then(data => {
              if (data.audio_data) {
                const audio = new Audio(data.audio_data);
                audio.volume = speakVolumeRef.current;
                audio.playbackRate = speakSpeedRef.current;
                currentAudioRef.current = audio;
                audio.onplay = () => setIsSpeaking(true);
                audio.onended = () => setIsSpeaking(false);
                audio.onpause = () => setIsSpeaking(false);
                audio.play().catch(e => console.warn('Audio play blocked:', e));
              }
            })
            .catch(err => {
              console.error('Proactive TTS error:', err);
              setIsSpeaking(false);
            });
        }
      } else if (message.type === "rtc_disconnected") {
        setDisconnecting(false);
        setConversing(false);
        setPhoneCalling(false);
        setIsTalking(false);
        setProcessing(false);
        toggleExpandChat(true);
        setAssistantVoiceMessage(null);
        setUserVoiceMessage(null);
        // Stay in current view (avatar or memory-chat) after disconnect
      } else if (message.type === "CONV_START") {
        convDetails.current = message.timestamp
      } else if (message.type === "CONV_END") {
        const duration = message.timestamp - convDetails.current
        setChat(chat => [...chat, {
          type: "CONVERSATION_EVENT",
          content: `Phone call duration: ${duration || 0} seconds`,
          timestamp: convDetails.current,
          duration: formatDuration(duration || 0)
        }])
        convDetails.current = null 
      } else if (message.type === "user_voice_message") {
        // Check if the message is actually new by comparing with the ref
        if (message.text !== prevUserMessageRef.current) {
          setUserMessageKey(prev => prev + 1);
          prevUserMessageRef.current = message.text;
        }
        setUserVoiceMessage(message.text);
        setLatestMessageType('user');
        setTimeout(() => {
          setAssistantVoiceMessage("...")
          if ("..." !== prevAssistantMessageRef.current) {
            setAssistantMessageKey(prev => prev + 1);
            prevAssistantMessageRef.current = "...";
          }
          setLatestMessageType('assistant');
        }, 200)
      } else if (message.type === "assistant_voice_message") {
        // Check if the message is actually new by comparing with the ref
        if (message.text !== prevAssistantMessageRef.current) {
          setAssistantMessageKey(prev => prev + 1);
          setVisemes(message.visemes)
          prevAssistantMessageRef.current = message.text;
        }
        setAssistantVoiceMessage(message.text);
        setLatestMessageType('assistant');
      } else if (message.type === "voice_message_start") {
        // Check if "..." is actually new
        if ("..." !== prevUserMessageRef.current) {
          setUserMessageKey(prev => prev + 1);
          prevUserMessageRef.current = "...";
        }
        setUserVoiceMessage("...");
        setLatestMessageType('user');
      } else if (message.type === "SESSION_NOT_FOUND") {
        console.log("Session not found, creating a new one");
        clearTimeout(wsReconnectRef.current);
        manualCloseRef.current = true;
        
        // Create a new session
        const res = await fetch(api + "/new_session", {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        const newSessionData = await res.json();
        setSessionId(newSessionData.session_id);
        
        // Close existing WebSocket
        if (wsRef.current) {
          wsRef.current.close();
        }

        // Create a new WebSocket connection with the new session ID
        (async () => {
          await createAndConnectWs(newSessionData.session_id, token, false);
        })();
      } else if (message.type === "FINISHED_TALK") {
        setAssistantTalking(false);
        if (fullEmoteRef.current) {
          // wait for the previous animations if any, to finish
          setTimeout(() => {
            setCurrentEmote(fullEmoteRef.current)
            fullEmoteRef.current = null;
          }, 250);
        }
      } else if (message.type === "STARTED_TALKING") {
        setAssistantTalking(true);
        if (microEmoteRef.current) {
          setTimeout(() => {
            setCurrentMicroExpression(microEmoteRef.current)
            microEmoteRef.current = null;
          }, 50)
        }
      } else if (message.type === "EMOTE_FOR_CONV") {
        if (message.emotional_response && message.emotional_response.response_type === "express_emote") {
          if (message.emotional_response.micro_emote) {
            setCurrentMicroExpression(message.emotional_response.micro_emote)
            microEmoteRef.current = message.emotional_response.micro_emote
          }
          if (message.emotional_response.full_emote) {
            setCurrentEmote(message.emotional_response.full_emote)
            fullEmoteRef.current = message.emotional_response.full_emote
          }

        }
      }  else if (message.type === "CONVERSATION_HISTORY") {
        const historyWithSeparators = processPhoneCallEvents(message.history);
        setChat(prevMessages => [...prevMessages, ...historyWithSeparators]);
              } else if (message.type === "vision_analysis") {
          // Handle vision analysis from the backend (no popup)
          setLastVisionAnalysis(message.analysis);
          // Vision analysis is stored but no popup is shown
      } else if (message.type === "memory_saved") {
        // Handle memory saved confirmation
        console.log("✅ Memory saved:", message.message);
        
        // Capture current memory IDs before refreshing (use ref to avoid stale closure)
        const oldMemoryIds = new Set((memoriesRef.current || []).map(m => m.id));
        
        // Refresh memories and highlight new ones
        if (token) {
          const newMemories = await fetchMemories();
          
          // Find newly added memories (IDs not in old set)
          const newlyAdded = (newMemories || []).filter(m => !oldMemoryIds.has(m.id));
          if (newlyAdded.length > 0) {
            console.log(`🌟 Highlighting ${newlyAdded.length} new memories in graph`);
            setHighlightedMemories(newlyAdded.map(m => ({
              id: m.id,
              text: m.text || '',
              category: m.category || 'general',
              score: 1.0
            })));
            // Auto-clear highlight after 10 seconds
            setTimeout(() => setHighlightedMemories([]), 10000);
          }
        }
        
        // Add a system message to chat showing the confirmation
        setChat(chat => {
          const updatedChat = [...chat];
          const currentTimestamp = Math.floor(Date.now() / 1000);
          
          // Add the confirmation message
          updatedChat.push({
            "role": "system",
            "content": message.message,
            "timestamp": currentTimestamp,
            "type": "memory_confirmation"
          });
          
          return updatedChat;
        });
      } else if (message.type === "ambient_memory_captured") {
        // Mobile capture saved new ambient memories — refresh graph + show chat follow-up
        console.log(`📡 Ambient capture: ${message.memories_extracted} memories, summary: ${message.summary}`);

        const oldMemoryIds = new Set((memoriesRef.current || []).map(m => m.id));

        if (token) {
          const newMemories = await fetchMemories();
          const newlyAdded = (newMemories || []).filter(m => !oldMemoryIds.has(m.id));
          if (newlyAdded.length > 0) {
            setHighlightedMemories(newlyAdded.map(m => ({
              id: m.id,
              text: m.text || '',
              category: m.category || 'ambient_capture',
              score: 1.0
            })));
            setTimeout(() => setHighlightedMemories([]), 10000);
          }
        }

        // Add a proactive chat message about the capture
        const captureMsg = message.memories_extracted > 0
          ? `📸 I just captured ${message.memories_extracted} new ${message.memories_extracted === 1 ? 'memory' : 'memories'} from your day: ${message.summary}`
          : `📸 I listened to a capture session but didn't find distinct memories. ${message.summary}`;

        setChat(chat => [...chat, {
          role: "assistant",
          content: captureMsg,
          timestamp: Math.floor(Date.now() / 1000),
          type: "ambient_capture_notification"
        }]);

      } else if (message.type === "AUDIO_MESSAGE") {
        // Create and store a blob URL for the audio
        const audioBase64 = message.audio;
        const binaryAudio = atob(audioBase64);
        const audioArray = new Uint8Array(binaryAudio.length);
        for (let i = 0; i < binaryAudio.length; i++) {
          audioArray[i] = binaryAudio.charCodeAt(i);
        }
        const audioBlob = new Blob([audioArray], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Add to chat with necessary metadata
        const newMessage = {
          role: "assistant",
          type: "AUDIO_MESSAGE",
          content: message.text,
          audioUrl: audioUrl,
          timestamp: Math.floor(Date.now() / 1000)
        };
        
        // Add emote type if it exists
        if (message.emote_type) {
          newMessage.emote_type = message.emote_type;
        }
        
        // Update the chat state
        setChat(chat => {
          // Check if we need to add a Today separator
          const updatedChat = [...chat];
          const currentTimestamp = Math.floor(Date.now() / 1000);
          
          // If we need a "Today" separator, add it
          // have to revisit this
          if (needsTodaySeparator(updatedChat)) {
            updatedChat.push({
              type: 'DATE_SEPARATOR',
              content: 'Today',
              timestamp: currentTimestamp
            });
          }
          
          // Add the new message
          updatedChat.push(newMessage);
          return updatedChat;
        });
        
        setLoadingChat(false);
      }
    };

    wsRef.current.onopen = () => {
      console.log("🔗 WebSocket connected successfully");
      toggleWsOpen(true);
      // Reset public share connecting flag when connection is established
      if (publicWsConnectingRef.current) {
        console.log("🔗 Resetting public share connecting flag");
        publicWsConnectingRef.current = false;
      }
    }

    wsRef.current.onerror = (error) => {
      console.error("🚫 WebSocket error:", error);
      // Reset public share connecting flag on error
      if (publicWsConnectingRef.current) {
        console.log("🔗 Resetting public share connecting flag on error");
        publicWsConnectingRef.current = false;
      }
    }

    wsRef.current.onclose = (event) => {
      console.log("🔗 WebSocket disconnected. Code:", event.code, "Reason:", event.reason);
      toggleWsOpen(false);
      setDisconnecting(false);
      setConversing(false);
      setPhoneCalling(false);
      setIsTalking(false);
      setProcessing(false);
      
      // Reset public share connecting flag on disconnect
      if (publicWsConnectingRef.current) {
        console.log("🔗 Resetting public share connecting flag on disconnect");
        publicWsConnectingRef.current = false;
      }
      
      // For public mode, don't auto-reconnect if the connection was rejected (codes 1002, 1003, 1008, 1011, 4401, 4402)
      if (isPublic && (event.code === 1002 || event.code === 1003 || event.code === 1008 || event.code === 1011 || event.code === 4401 || event.code === 4402)) {
        console.log("🚫 Public share WebSocket connection rejected by server. Code:", event.code);
        return; // Don't reconnect
      }
      
      if (!manualCloseRef.current) {
        wsReconnectRef.current = setTimeout(async () => {  
          await createAndConnectWs(currentSessionId, currentToken, isPublic)
        }, WS_RECONNECT_TIMEOUT);
      } else {
        manualCloseRef.current = false; // Reset flag
      }
    }
  }

  // Combined new_session and proactive message call (only after verification)
  useEffect(() => {
    if (!loading && token && user && user.emailVerified) {
      const createSession = async () => {
        if (!sessionId) {
          try {
            // Start the new session API call
          const newSessionPromise = fetch(api + `/new_session`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(res => res.json());

            // Start fetchConversationHistory (assumed to be async and handle its own state)
            const historyPromise = token ? fetchConversationHistory() : Promise.resolve();

            // Wait for both to complete
            const [newSessionData] = await Promise.all([newSessionPromise, historyPromise]);

            setChatLoading(false);

            // Set sessionId and create WebSocket connection
            setSessionId(newSessionData.session_id);
            console.log('envs are', newSessionData.environments)
            if (newSessionData.environments && newSessionData.environments.length) {
              setEnvironments([...environments, ...newSessionData.environments])
              setAreEnvsLoaded(true)
            } else {
              setAreEnvsLoaded(true)
            }

            if (!newSessionData.model_selection) {
              setShowModelSelectionScreen(true);
              setSelectedModel("Synth")
              return;
            } else {
              apiSelectedModel.current = newSessionData.model_selection
              setSelectedModel(newSessionData.model_selection)
            }

            await new Promise(resolve => setTimeout(resolve, 100));

            await createAndConnectWs(newSessionData.session_id, token, false);
          } catch (error) {
            console.error("Error creating session and proactive message:", error);
          }
        }
      };
      createSession();
    }
  }, [loading, token, user?.emailVerified]);

  useEffect(() => {
    if (!loading) {
      // Don't override mode if we're in public mode
      if (isPublicMode) {
        setMode(APP_MODE); // Ensure APP_MODE is set for public shares
        return;
      }
      setMode(!token ? INTRO_MODE : APP_MODE)
    }
  }, [loading, isPublicMode, token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (token && !isPublicMode) {  // Don't fetch preferences for public mode
          fetchMemories();
          const prefsRes = await fetch(`${api}/user_preferences`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (prefsRes.status === 401 || prefsRes.status === 403) {
            // A single 401 is often a transient/expired-access blip. Try one
            // token refresh and let the resulting token change re-run this
            // effect before we hard-logout the user (which reloads the page).
            if (prefsAuthRetriedRef.current) {
              console.warn("user_preferences still 401 after refresh — session is dead, logging out");
              prefsAuthRetriedRef.current = false;
              logout();
              return;
            }
            console.warn("user_preferences 401/403 — refreshing token once before logout");
            prefsAuthRetriedRef.current = true;
            const { refreshAuthToken } = await import('./auth/firebase');
            const newToken = await refreshAuthToken();
            if (!newToken) {
              // Couldn't get a fresh token and access is rejected → genuinely dead.
              prefsAuthRetriedRef.current = false;
              logout();
            }
            // On success, refreshAuthToken updates token state, which re-runs
            // this effect with the new token (retry happens there).
            return;
          }
          // Reached a good response — reset the retry guard for next time.
          prefsAuthRetriedRef.current = false;
          const prefsData = await prefsRes.json();

          setIsMemoryEnabled(prefsData.memory_enabled !== false); // Default to true if not set
          setIsChatEnabled(prefsData.chat_enabled !== false); // Default to true if not set
          setAiAutoCallsEnabled(prefsData.ai_auto_calls_enabled !== false); // Default to true
          setCustomAvatarUrl(prefsData.custom_avatar_url || ""); // Set custom avatar URL
          setCustomAvatarInput(prefsData.custom_avatar_url || ""); // Set input field value
          setCustomVoiceId(prefsData.custom_voice_id || ""); // Set custom voice ID
          setCustomVoiceInput(prefsData.custom_voice_id || ""); // Set voice input field value
          setTtsProvider(prefsData.tts_provider || "dxtlabs"); // TTS provider preference
          setMemoryOwnerName(prefsData.memory_owner_name || ""); // Set memory owner name
          setMemoryOwnerDescription(prefsData.memory_owner_description || ""); // Set memory owner description
          setUserLocation(prefsData.location || ""); // Set user's city (time + weather context)
          if (!prefsData.onboarding_complete) setShowOnboarding(true);
          setLoadingPreferences(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
  
    if (token && !isPublicMode) {
      fetchData();
    }
  }, [token, isPublicMode]);

  const renderActivityIcon = () => {
    if (processing) {
      return <LuBrainCog style={{fontSize: 21}} />
    }

    return <RiVoiceprintFill style={{fontSize: isMobile ? 18 : 21}} />
  }

  // Share link management functions
  const fetchShareLinks = async () => {
    if (!token) return;
    
    setLoadingShareLinks(true);
    try {
      const response = await fetch(`${api}/share_links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setShareLinks(data.share_links || []);
      }
    } catch (error) {
      console.error("Error fetching share links:", error);
    } finally {
      setLoadingShareLinks(false);
    }
  };

  const createShareLink = async (includeMemories = true, selectedCategories = null, expiresAt = null) => {
    if (!token) return;
    
    setCreatingShareLink(true);
    try {
      const response = await fetch(`${api}/create_share_link`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          include_memories: includeMemories,
          selected_categories: selectedCategories,
          expires_at: expiresAt 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        await fetchShareLinks(); // Refresh the list
        return data;
      }
    } catch (error) {
      console.error("Error creating share link:", error);
    } finally {
      setCreatingShareLink(false);
    }
  };

  const deleteShareLink = async (shareId) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${api}/share_links/${shareId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchShareLinks(); // Refresh the list
      }
    } catch (error) {
      console.error("Error deleting share link:", error);
    }
  };

  const copyShareLink = (shareId) => {
    const shareUrl = `${window.location.origin}/share/${shareId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert("Share link copied to clipboard!");
    });
  };

  // Load share links when user logs in
  useEffect(() => {
    if (token && showMenu) {
      fetchShareLinks();
    }
  }, [token, showMenu]);

  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
  };

  const handleSaveMemoryOwner = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${api}/update_preferences`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          memory_owner_name: memoryOwnerName,
          memory_owner_description: memoryOwnerDescription,
          location: userLocation
        })
      });
      
      if (response.ok) {
        console.log("Memory owner settings saved successfully");
        showToast("Memory owner settings saved successfully", "success");
      } else {
        showToast("Failed to save memory owner settings", "error");
      }
    } catch (error) {
      console.error("Error saving memory owner settings:", error);
      showToast("Error saving memory owner settings", "error");
    }
  };

  const handleKeyDown = (e) => {
    // Add any additional logic you want to execute when a key is pressed
    console.log('Key pressed:', e.key);
  };

  const handleSaveConversation = () => {
    if (!conversationToSave) return;

    const blob = new Blob([JSON.stringify(conversationToSave, null, 2)], { type: 'application/json' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation_${sessionId}_${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowSaveDialog(false);
      setConversationToSave(null);
    }, 100);
  };

  const handleDisconnect = async () => {
    if (!sessionId) return;

    setShowVideo(false);
    
    setDisconnecting(true);
    
    try {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }

      // Close local tracks
      if (peerConnectionRef.current) {
        const senders = peerConnectionRef.current.getSenders();
        senders.forEach(sender => {
          if (sender.track) {
            sender.track.stop();
          }
        });
        
        // Close the peer connection
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // Clean up local stream
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      
      // Reset audio context
      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      if (analyserRef.current) {
        analyserRef.current = null;
      }
      
      // Tell the server to clean up this session's connections - we are not really disconnecting everything
      // Tell the server to clean up this session's WebRTC connection
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "rtc_disconnect",
          sessionId
        }));
      }
    } catch (error) {
      console.error("Error during disconnection:", error);
      setDisconnecting(false);
    }
  };

  const handleMemoryToggle = async (value, ntoken) => {
    if (ntoken) {
      setIsTogglingMemory(true);
      try {
        const response = await fetch(`${api}/update_preferences`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${ntoken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ 
            memory_enabled: value 
          })
        });
        
        if (response.ok) {
          setIsMemoryEnabled(value);
          showToast(value ? "Memory enabled" : "Memory disabled", "success");
        } else {
          showToast("Failed to update memory setting", "error");
        }
      } catch (error) {
        console.error("Error updating memory preference:", error);
        showToast("Error updating memory setting", "error");
      } finally {
        setIsTogglingMemory(false);
      }
    }
  };
  
  const handleChatToggle = async (value, ntoken) => {
    if (ntoken) {
      setIsTogglingChat(true);
      try {
        const response = await fetch(`${api}/update_preferences`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${ntoken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ 
            chat_enabled: value 
          })
        });
        
        if (response.ok) {
          setIsChatEnabled(value);
          showToast(value ? "Chat enabled" : "Chat disabled", "success");
        } else {
          showToast("Failed to update chat setting", "error");
        }
      } catch (error) {
        console.error("Error updating chat preference:", error);
        showToast("Error updating chat setting", "error");
      } finally {
        setIsTogglingChat(false);
      }
    }
  };
  
  const handleMemoryOwnerUpdate = async (ntoken) => {
    if (!ntoken) return;
    
    setIsUpdatingMemoryOwner(true);
    setMemoryOwnerError("");
    setMemoryOwnerSuccess("");
    
    try {
      const response = await fetch(`${api}/update_preferences`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ntoken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ 
          memory_owner_name: memoryOwnerName,
          memory_owner_description: memoryOwnerDescription
        })
      });
      
      if (response.ok) {
        setMemoryOwnerSuccess("Memory owner information updated successfully.");
        setTimeout(() => setMemoryOwnerSuccess(""), 3000); // Clear success message after 3 seconds
      } else {
        setMemoryOwnerError("Failed to update memory owner information.");
      }
    } catch (error) {
      console.error("Error updating memory owner information:", error);
      setMemoryOwnerError("Error updating memory owner information.");
    } finally {
      setIsUpdatingMemoryOwner(false);
    }
  };

  const handleCustomAvatarUpdate = async (avatarUrl) => {
    setIsUpdatingAvatar(true);
    setAvatarError("");
    setAvatarSuccess("");
    
    try {
      const response = await fetch(`${api}/update_custom_avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          avatar_url: avatarUrl
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Force avatar refresh by temporarily hiding then showing model
        setIsModelVisible(false);
        
        // Update avatar URL and force refresh
        setCustomAvatarUrl(avatarUrl);
        setCustomAvatarInput(avatarUrl);
        setAvatarError("");
        setAvatarRefreshKey(prev => prev + 1); // Force model re-render
        
        // Show success message
        setAvatarSuccess(avatarUrl ? "Avatar updated successfully!" : "Switched to default avatar!");
        showToast(avatarUrl ? "Avatar updated successfully!" : "Switched to default avatar!", "success");
        
        // Restore model visibility after a brief delay to ensure proper refresh
        setTimeout(() => {
          setIsModelVisible(true);
        }, 100);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setAvatarSuccess("");
        }, 3000);
        
      } else {
        setAvatarError(data.detail || "Failed to update avatar");
      }
    } catch (error) {
      console.error('Error updating custom avatar:', error);
      setAvatarError("Failed to update avatar");
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const handleCustomVoiceUpdate = async (voiceId) => {
    setIsUpdatingVoice(true);
    setVoiceError("");
    setVoiceSuccess("");
    
    try {
      const response = await fetch(`${api}/update_custom_voice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          voice_id: voiceId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Update voice ID
        setCustomVoiceId(voiceId);
        setCustomVoiceInput(voiceId);
        setVoiceError("");
        
        // Show success message
        setVoiceSuccess(voiceId ? "Voice ID updated successfully!" : "Switched to default voice!");
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setVoiceSuccess("");
        }, 3000);
        
      } else {
        setVoiceError(data.detail || "Failed to update voice ID");
      }
    } catch (error) {
      console.error('Error updating custom voice:', error);
      setVoiceError("Failed to update voice ID");
    } finally {
      setIsUpdatingVoice(false);
    }
  };

  const handleTtsProviderUpdate = async (provider) => {
    if (provider === ttsProvider) return;
    setIsUpdatingTtsProvider(true);
    setVoiceError("");
    setVoiceSuccess("");
    try {
      const response = await fetch(`${api}/update_tts_provider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ tts_provider: provider }),
      });
      const data = await response.json();
      if (response.ok) {
        setTtsProvider(provider);
        setVoiceSuccess(`TTS provider set to ${provider}`);
        setTimeout(() => setVoiceSuccess(""), 3000);
      } else {
        setVoiceError(data.detail || "Failed to update TTS provider");
      }
    } catch (error) {
      console.error('Error updating TTS provider:', error);
      setVoiceError("Failed to update TTS provider");
    } finally {
      setIsUpdatingTtsProvider(false);
    }
  };


  const shouldShowModelLoader = () => 
    modelLoading && isSmallSize 
      ? !showIntroMode 
      : modelLoading;

  const renderConversing = () => {
    if (!isWsOpen) {
      return null
    }
  
    if (!conversing) {
      return (
        <div 
          className="lets-connect-button"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: isMobile ? 6 : 12,
            position: 'relative',
            transition: "transform 0.3s ease"
          }}
          onClick={(e) => {
            if (!isFirefox) {
              e.stopPropagation();
              stopSpeaking();
              setIsCallDropdownVisible(false);
              toggleExpandChat(false);
              setAppView('avatar');
              setPhoneCalling(true);
              setConnectAnimationActive(true);
              // Reset connect animation after 3 seconds
              setTimeout(() => setConnectAnimationActive(false), 3000);
              initiateWebRTC(true); // Enable video for vision analysis
              clearInterval(progressIntervalRef.current);
              setShowTipCard(false);
            }
          }}
          onMouseEnter={(e) => {
            if (isFirefox) {
              setShowFirefoxTooltip(true);
            }
          }}
          onMouseLeave={(e) => {
            if (isFirefox) {
              setShowFirefoxTooltip(false);
            }
          }}
        >
          <LoadingDiv
            isLoading={calling}
            duration={0.75} 
            width={`${isMobile ? 36 : 46}px`}
            height={`${isMobile ? 36 : 46}px`}
            borderWidth={1}
            loadingColor="#FFFFFF"
            borderColor="rgba(255, 255, 255, 0.5)"
            borderRadius={`${46}px`}
            backgroundColor="transparent"
            loadingSegmentPercentage={25}
          >
            <HiOutlinePhone style={{ fontSize: isMobile ? 18 : 21 }} />
          </LoadingDiv>
          <div style={{ marginLeft: isMobile ? 7 : "1rem", marginRight: "0.5rem", fontSize: isMobile ? "15px" : "18px" }}>
            {calling ? "Connecting..." : "Start Voice Call"}
          </div>
          
          {/* Firefox Warning Tooltip */}
          {isFirefox && showFirefoxTooltip && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '14px',
                maxWidth: '250px',
                textAlign: 'center',
                zIndex: 1000,
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                pointerEvents: 'none',
                width: 300
              }}
            >
              Unfortunately live WebRTC connection is not available for Firefox at this moment
              {/* Tooltip arrow */}
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid rgba(0, 0, 0, 0.85)',
                }}
              />
            </div>
          )}
        </div>
      )
    }
  
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 12
      }}>
        <div style={{ marginRight: "1rem", marginLeft: "0.5rem", fontSize: isMobile ? 15 : "18px" }}>
          {processing ? "Processing thoughts" : assistantTalking ? "🔇 Mic muted - AI speaking" : "I'm listening..."}
        </div>
        <LoadingDiv
          isLoading={processing} 
          duration={0.75} 
          width={`${isMobile ? 36 : 46}px`}
          height={`${isMobile ? 36 : 46}px`}
          borderWidth={1}
          loadingColor="#FFFFFF"
          borderColor="rgba(255, 255, 255, 0.5)"
          borderRadius={`${46}px`}
          backgroundColor="transparent"
          loadingSegmentPercentage={25}
          isGlowing
        >
          {renderActivityIcon()}
        </LoadingDiv>
      </div>
    )
  }

  const CHAT_CHAR_DISPLAY = isMobile ? 125 : isSmallSize ? 300 : 700

  const renderUserMessage = (key) => (
    <div style={{
      display: "flex",
      maxWidth: userVoiceMessage === "..." ? "none" : "calc(100% - 20px)",
      alignSelf: "flex-end", 
      marginTop: 10,
      padding: 8,
      boxSizing: "border-box",
      backgroundColor: 'rgba(100, 150, 255, 1)', // Blue background
      border: '1px solid rgba(255, 255, 255, 0.4)',
      borderRadius: 12,
      transition: "max-height 0.3s ease, max-width 0.3s ease"
    }}
    key={`user-${key}`}
    className="userSlideIn"
    >
      <div style={{
        color: 'white',
        boxSizing: "border-box",
        fontSize: 14,
        lineHeight: "18px",
        letterSpacing: "0.5px",
        overflow: isTranscriptionExpanded ? "auto" : "hidden",
        textWrap: isTranscriptionExpanded ? "wrap" : "nowrap",
        textOverflow: "ellipsis"
      }}
      >
        {userVoiceMessage}
      </div>
    </div>
  );

  const getStylesForVideo = () => {
    const commonStyles = {
      position: "absolute",
      borderRadius: "8px",
      overflow: "hidden",
      border: "1px solid rgba(255, 255, 255, 0.6)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      background: 'rgba(0, 0, 0, 0.3)'
    }

    if (isMobile) {
      return {
        ...commonStyles,
        bottom: 200,
        left: 20,
        width: 120,
        height: 120
      }
    }

    if (windowWidth < 1200 && windowWidth > 900) {
      return {
        ...commonStyles,
        top: 100,
        right: 20,
        width: 280,
        height: 170
      }
    }

    if (windowWidth < 900) {
      return {
        ...commonStyles,
        top: 240,
        left: 20,
        width: 280,
        height: 170
      }
    }

    return {
      ...commonStyles,
      bottom: 350,
      right: 20,
      width: 280,
      height: 170
    }
  }

  // Function to render assistant message
  const renderAssistantMessage = (key) => (
    <div style={{
      display: "flex",
      maxWidth: assistantMessage === "..." ? "none" : "100%",
      alignSelf: "flex-end",
      marginTop: 10,
      backgroundColor: 'rgba(50, 50, 50, 0.55)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      padding: 8,
      borderRadius: 12,
      transition: "max-height 0.3s ease, max-width 0.3s ease",
      boxSizing: "border-box"
    }}
    key={`assistant-${key}`}
    className="assistantSlideIn"
    >
      <div style={{
        flex: 1,
        color: "white",
        fontSize: 14,
        overflow: isTranscriptionExpanded ? "auto" : "hidden",
        textWrap: isTranscriptionExpanded ? "wrap" : "nowrap",
        lineHeight: "18px",
        letterSpacing: "0.5px",
        textOverflow: "ellipsis"
      }}
      >
        {assistantMessage}
      </div>
    </div>
  );

  if (loading && !isPublicMode) {
    return (
      <div
        className="App"
        style={{
          position: "relative",
          background: "transparent",
          height: "100%",
          width: "100%",
          background: "black"
        }}
      />
    )
  }

  const renderMenu = () => {
    return (
      <div style={{
        position: "absolute",
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
        background: 'rgba(20, 20, 20, 0.25)',
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 99999,
      }}>
      <div style={{
        position: "absolute",
        top: isMobile ? 10 : 15,
        width: isMobile ? 350 : 400,
        right: isMobile ? 10 : 25,
        boxSizing: "border-box",
        bottom: isMobile ? 10 : 15,
        borderRadius: 15,
        background: 'rgba(38, 34, 28, 0.98)',
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        boxSizing: "border-box",
        flexDirection: "column",
      }}>
        <div style={{
          width: "100%"
        }}>
        <div style={{
          position: "absolute",
          top: 1,
          right: 1,
          width: 200,
          height: 45,
          background: "rgba(48, 43, 35, 0.95)",
          fontSize: 17,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          borderTopRightRadius: 13,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 8,
          color: "white",
          fontWeight: "bold",
          letterSpacing: "0.5px"
        }}>
          <span>Settings</span>
        </div>
        </div>
        <div className="convert-white-hover" style={{
          position: "absolute",
          top: 5,
          left: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "6px 12px",
          borderRadius: 15,
          cursor: "pointer"
        }} onClick={() => {
          setShowMenu(false);
        }}>
          <span style={{
            fontSize: 16
          }}>Close</span>
          <IoIosCloseCircleOutline style={{
            fontSize: 25,
            marginLeft: 8
          }} />
        </div>
        
        <div ref={el => { if (el) el.scrollTop = 0; }} style={{
          marginTop: 70,
          width: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 70px)"
        }}>
          <div style={{
            marginRight: 15,
            fontSize: 25
          }}>
            <span style={{
              fontWeight: "bold"
            }}>Hello</span> {user.displayName || user.email}
          </div>
          {/* Quick-jump navigation pills */}
          <div style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            padding: '12px 25px 0',
            marginTop: 8
          }}>
            {[
              { id: 'settings-model', label: 'Model' },
              { id: 'settings-avatar', label: 'Avatar' },
              { id: 'settings-voice', label: 'Voice' },
              { id: 'settings-owner', label: 'Soul Owner' },
              { id: 'settings-share', label: 'Share' },
              { id: 'settings-conversations', label: 'Chats' },
              { id: 'settings-data', label: 'Data' },
              { id: 'settings-account', label: 'Account' },
            ].map(item => (
              <div
                key={item.id}
                onClick={() => {
                  const el = document.getElementById(item.id);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.2px',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'rgba(59,114,95,0.15)'; e.currentTarget.style.borderColor = 'rgba(59,114,95,0.3)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
              >
                {item.label}
              </div>
            ))}
          </div>
          <div style={{
            width: "100%",
            padding: 25,
            paddingTop: 20,
            boxSizing: 'border-box',
            display: "flex",
            flexDirection: "column"
          }}>
            <div style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column"
            }}>
              <div id="settings-model" style={{
                fontSize: 18
              }}>Memory Preservation</div>
              <div style={{
                width: "100%",
                height: 1,
                marginTop: 8,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }} />
              <div style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                paddingTop: 8,
                marginBottom: 25,
                boxSizing: "border-box"
              }}>
                        <img src={selectedModel === "Synth" ? "fun-icon.png" : "philosophical-icon.png"} style={{
          width: selectedModel === "Synth" ? 40 : 55,
          marginRight: selectedModel === "Synth" ? 8 : 0,
          marginTop: selectedModel === "Synth" ? 8 : 0,
                  alignSelf: 'flex-start'
                }} />
                <div style={{
                  marginLeft: 8,
                  marginTop: 5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start"
                }}>
                  <div style={{
                    fontWeight: "bold"
                  }}>{selectedModel === "Synth" ? "Legacy" : "Story"}</div>
                  <div style={{
                    fontSize: 14,
                    textAlign: "left",
                    marginTop: 5
                  }}>
                  {selectedModel === "Synth" ? "Best for relaxed, supportive chats focused on relationships and self-reflection." : "Best for preserving your memories and reflecting on meaningful life experiences."}
                  </div>
                  <div style={{width: "100%", display: "flex"}}>
                    <div style={{
                      marginTop: 8,
                      width: 80,
                      height: 28,
                      background: 'linear-gradient(135deg, #3b725f, #4a8a73)',
                      borderRadius: 5,
                      color: "white",
                      display: "flex",
                      fontSize: 14,
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "transform 0.3s ease"
                    }} onMouseOver={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }} onClick={() => {
                      setShowMenu(false);
                      setShowModelSelectionScreen(true);
                    }}>
                      <span>Change</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column"
            }}>
              <div style={{
                fontSize: 18
              }}>Environment</div>
              <div style={{
                width: "100%",
                height: 1,
                marginTop: 8,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }} />
              
              <EnvironmentCarousel 
                environments={environments} 
                selectedEnv={selectedEnv} 
                setSelectedEnv={(env) => { envUserChangedRef.current = true; setSelectedEnv(env); }} 
              />
              
            </div>
            <div style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column"
            }}>
              <div id="settings-avatar" style={{
                fontSize: 18
              }}>Custom Avatar</div>
              <div style={{
                width: "100%",
                height: 1,
                marginTop: 8,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }} />
              <div style={{
                width: "100%",
                paddingTop: 15,
                marginBottom: 25
              }}>
                <div style={{
                  fontSize: 14,
                  marginBottom: 10,
                  color: "rgba(255, 255, 255, 0.8)"
                }}>
                  Enter a URL to a GLB file to use as your custom avatar:
                </div>
                <div style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <input
                    value={customAvatarInput}
                    onChange={(e) => setCustomAvatarInput(e.target.value)}
                    placeholder="https://example.com/avatar.glb"
                    style={{
                      width: "100%",
                      padding: 10,
                      boxSizing: "border-box",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "white"
                    }}
                  />
                  {avatarError && (
                    <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "rgba(255,80,80,0.15)", color: "#f66", fontSize: 12 }}>
                      {avatarError}
                    </div>
                  )}
                  {avatarSuccess && (
                    <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "rgba(76,175,80,0.15)", color: "#4caf50", fontSize: 12 }}>
                      {avatarSuccess}
                    </div>
                  )}
                  <div style={{
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8
                  }}>
                    {customAvatarUrl && (
                      <div
                        style={{
                          padding: "6px 12px",
                          borderRadius: 6,
                          background: "rgba(255,80,80,0.15)",
                          color: "#f66",
                          fontSize: 14,
                          cursor: "pointer",
                          border: "1px solid rgba(255,80,80,0.3)"
                        }}
                        onClick={() => handleCustomAvatarUpdate("")}
                      >
                        Reset to Default
                      </div>
                    )}
                    <div
                      style={{
                        padding: "6px 12px",
                        borderRadius: 6,
                        background: isUpdatingAvatar ? "rgba(59,114,95,0.3)" : "linear-gradient(135deg, #3b725f, #4a8a73)",
                        color: "white",
                        fontSize: 14,
                        cursor: isUpdatingAvatar ? "wait" : "pointer",
                        opacity: isUpdatingAvatar ? 0.6 : 1
                      }}
                      onClick={() => !isUpdatingAvatar && handleCustomAvatarUpdate(customAvatarInput)}
                    >
                      {isUpdatingAvatar ? "Applying..." : "Apply"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column"
            }}>
              <div id="settings-voice" style={{
                fontSize: 18
              }}>Voice Settings</div>
              <div style={{
                width: "100%",
                height: 1,
                marginTop: 8,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }} />
              <div style={{
                width: "100%",
                paddingTop: 15,
                marginBottom: 25
              }}>
                <div style={{
                  fontSize: 14,
                  marginBottom: 10,
                  color: "rgba(255, 255, 255, 0.8)"
                }}>
                  Clone your voice or use a custom ElevenLabs voice:
                </div>
                <div style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10
                }}>
                  <button
                    style={{
                      padding: "12px 20px",
                      borderRadius: 8,
                      background: "linear-gradient(135deg, #3b725f 0%, #bd7b69 100%)",
                      color: "white",
                      border: "none",
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.3s ease"
                    }}
                    onClick={() => setShowVoiceCloningModal(true)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 114, 95, 0.4)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    Open Voice Cloning Studio
                  </button>
                  
                  {customVoiceId && (
                    <div style={{
                      padding: 10,
                      background: "rgba(76, 175, 80, 0.1)",
                      border: "1px solid rgba(76, 175, 80, 0.3)",
                      borderRadius: 6,
                      fontSize: 13,
                      color: "#4CAF50"
                    }}>
                      ✓ Using custom voice: {customVoiceId.substring(0, 8)}...
                    </div>
                  )}
                  
                  <div style={{
                    marginTop: 14,
                    fontSize: 12,
                    color: "rgba(255, 255, 255, 0.7)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    TTS Provider
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {[
                      { value: "dxtlabs", label: "dxtlabs" },
                      { value: "elevenlabs", label: "ElevenLabs" },
                      { value: "openai", label: "OpenAI" },
                    ].map(({ value, label }) => {
                      const active = ttsProvider === value;
                      return (
                        <div
                          key={value}
                          onClick={() => !isUpdatingTtsProvider && handleTtsProviderUpdate(value)}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            borderRadius: 6,
                            border: `1px solid ${active ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.15)"}`,
                            background: active ? "rgba(76,175,80,0.15)" : "rgba(255,255,255,0.04)",
                            color: active ? "#4caf50" : "rgba(255,255,255,0.7)",
                            fontSize: 13,
                            textAlign: "center",
                            cursor: isUpdatingTtsProvider ? "wait" : "pointer",
                            opacity: isUpdatingTtsProvider ? 0.6 : 1,
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                  {customVoiceId && ttsProvider !== "elevenlabs" && (
                    <div style={{ marginTop: 6, fontSize: 11, color: "#e8c77b", lineHeight: 1.4 }}>
                      Note: a custom voice ID is set, so ElevenLabs will be used automatically when in Legacy mode regardless of this preference.
                    </div>
                  )}

                  <div style={{
                    marginTop: 14,
                    padding: 10,
                    background: "rgba(255, 255, 255, 0.05)",
                    borderRadius: 6,
                    fontSize: 12,
                    color: "rgba(255, 255, 255, 0.6)"
                  }}>
                    Or enter a voice ID manually:
                  </div>
                  <input
                    value={customVoiceInput}
                    onChange={(e) => setCustomVoiceInput(e.target.value)}
                    placeholder="Enter ElevenLabs voice ID"
                    style={{
                      width: "100%",
                      padding: 10,
                      boxSizing: "border-box",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "white"
                    }}
                  />
                  {voiceError && (
                    <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "rgba(255,80,80,0.15)", color: "#f66", fontSize: 12 }}>
                      {voiceError}
                    </div>
                  )}
                  {voiceSuccess && (
                    <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "rgba(76,175,80,0.15)", color: "#4caf50", fontSize: 12 }}>
                      {voiceSuccess}
                    </div>
                  )}
                  <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    marginTop: 6
                  }}>
                    {customVoiceId && (
                      <div
                        style={{
                          padding: "6px 12px",
                          borderRadius: 6,
                          background: "rgba(255,80,80,0.15)",
                          color: "#f66",
                          fontSize: 14,
                          cursor: "pointer",
                          border: "1px solid rgba(255,80,80,0.3)"
                        }}
                        onClick={() => handleCustomVoiceUpdate("")}
                      >
                        Reset to Default
                      </div>
                    )}
                    <div
                      style={{
                        padding: "6px 12px",
                        borderRadius: 6,
                        background: isUpdatingVoice ? "rgba(59,114,95,0.3)" : "linear-gradient(135deg, #3b725f, #4a8a73)",
                        color: "white",
                        fontSize: 14,
                        cursor: isUpdatingVoice ? "wait" : "pointer",
                        opacity: isUpdatingVoice ? 0.6 : 1
                      }}
                      onClick={() => !isUpdatingVoice && handleCustomVoiceUpdate(customVoiceInput)}
                    >
                      {isUpdatingVoice ? "Applying..." : "Apply Voice ID"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column"
            }}>
              <div id="settings-owner" style={{
                fontSize: 18
              }}>Soul Owner</div>
              <div style={{
                width: "100%",
                height: 1,
                marginTop: 8,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }} />
              <div style={{
                width: "100%",
                paddingTop: 15,
                marginBottom: 25
              }}>
                <div style={{
                  fontSize: 14,
                  marginBottom: 10,
                  color: "rgba(255, 255, 255, 0.8)"
                }}>
                  Set your name and description for memory storage:
                </div>
                <div style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <input
                    value={memoryOwnerName}
                    onChange={(e) => setMemoryOwnerName(e.target.value)}
                    placeholder="Your name"
                    style={{
                      width: "100%",
                      padding: 10,
                      boxSizing: "border-box",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "white",
                      marginBottom: 10
                    }}
                  />
                  <textarea
                    value={memoryOwnerDescription}
                    onChange={(e) => setMemoryOwnerDescription(e.target.value)}
                    placeholder="Brief description about yourself"
                    style={{
                      width: "100%",
                      padding: 10,
                      boxSizing: "border-box",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "white",
                      height: 80,
                      resize: "none"
                    }}
                  />
                  <input
                    value={userLocation}
                    onChange={(e) => setUserLocation(e.target.value)}
                    placeholder="Your city (e.g. New York) — for the right time & weather"
                    style={{
                      width: "100%",
                      padding: 10,
                      boxSizing: "border-box",
                      borderRadius: 6,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      background: "rgba(255, 255, 255, 0.05)",
                      color: "white",
                      marginTop: 10
                    }}
                  />
                  <div style={{
                    marginTop: 10,
                    display: "flex",
                    justifyContent: "flex-end"
                  }}>
                    <div
                      style={{
                        padding: "6px 12px",
                        borderRadius: 6,
                        background: "linear-gradient(135deg, #3b725f, #4a8a73)",
                        color: "white",
                        fontSize: 14,
                        cursor: "pointer"
                      }}
                      onClick={handleSaveMemoryOwner}
                    >
                      Save
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column"
            }}>
              <div id="settings-share" style={{
                fontSize: 18
              }}>Share</div>
              <div style={{
                width: "100%",
                height: 1,
                marginTop: 8,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
              }} />
              <div style={{
                width: "100%",
                paddingTop: 15,
                marginBottom: 25
              }}>
                <div style={{
                  fontSize: 14,
                  marginBottom: 10,
                  color: "rgba(255, 255, 255, 0.8)"
                }}>
                  Create shareable links to your Legacy:
                </div>
                <div style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 15
                }}>
                  <div
                    style={{
                      padding: "8px 14px",
                      borderRadius: 6,
                      background: "linear-gradient(135deg, #3b725f, #4a8a73)",
                      color: "white",
                      fontSize: 14,
                      cursor: creatingShareLink ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      opacity: creatingShareLink ? 0.7 : 1
                    }}
                    onClick={() => setShowShareLinkCreator(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                      <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/>
                    </svg>
                    {creatingShareLink ? "Creating..." : "Create Share Link"}
                  </div>
                </div>
                {loadingShareLinks ? (
                  <div style={{ textAlign: "center", padding: 20 }}>Loading share links...</div>
                ) : (
                  <div style={{ width: "100%" }}>
                    {shareLinks.map(link => (
                      <div key={link.share_id} style={{
                        padding: 12,
                        borderRadius: 8,
                        background: "rgba(255, 255, 255, 0.05)",
                        marginBottom: 10,
                        border: "1px solid rgba(255, 255, 255, 0.1)"
                      }}>
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8
                        }}>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}>
                            <div style={{ fontWeight: "bold" }}>
                              {new Date(link.created_at).toLocaleDateString()}
                            </div>
                            <div style={{
                              fontSize: 12,
                              opacity: 0.7,
                              background: "rgba(255, 255, 255, 0.1)",
                              padding: "2px 8px",
                              borderRadius: 12
                            }}>
                              {link.access_count || 0} visits
                            </div>
                          </div>
                          
                          <div style={{
                            fontSize: 13,
                            opacity: 0.8,
                            padding: "6px 8px",
                            background: "rgba(0, 0, 0, 0.2)",
                            borderRadius: 4,
                            fontFamily: "monospace",
                            wordBreak: "break-all"
                          }}>
                            {window.location.origin}/share/{link.share_id}
                          </div>
                          
                          <div style={{
                            display: "flex",
                            gap: 8,
                            justifyContent: "flex-end",
                            marginTop: 4
                          }}>
                            <button
                              onClick={() => copyShareLink(link.share_id)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "none",
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                color: "white",
                                fontSize: 12,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 4
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                                <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                              </svg>
                              Copy Link
                            </button>
                            <button
                              onClick={() => deleteShareLink(link.share_id)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "none",
                                backgroundColor: "rgba(255, 107, 107, 0.3)",
                                color: "white",
                                fontSize: 12,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 4
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* ── Account & Data ── */}
          <div style={{ width: '100%', margin: '10px 0 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>Account & Data</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Conversation History */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <div id="settings-conversations" style={{ fontSize: 18 }}>Conversations</div>
            <div style={{ width: '100%', height: 1, marginTop: 8, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }} />
            <div style={{ width: '100%', paddingTop: 15, marginBottom: 25 }}>
              <div style={{ fontSize: 14, marginBottom: 10, color: 'rgba(255, 255, 255, 0.8)' }}>
                Search, export, or clear your conversation history:
              </div>
              <div
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #3b725f, #4a8a73)',
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'opacity 0.2s'
                }}
                onClick={() => { setShowMenu(false); setShowConversationManager(true); }}
              >
                Manage Conversations
              </div>
            </div>
          </div>
          
          {/* Soul & Insights */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <div style={{ fontSize: 18 }}>Soul & Insights</div>
            <div style={{ width: '100%', height: 1, marginTop: 8, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }} />
            <div style={{ width: '100%', paddingTop: 15, marginBottom: 25 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(59,114,95,0.25), rgba(109,188,154,0.15))',
                    border: '1px solid rgba(109,188,154,0.25)',
                    color: 'rgba(109,188,154,0.95)',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowSoulOrb(true); }}
                >
                  ◉ Soul Profile
                </div>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(168,130,214,0.2), rgba(168,130,214,0.1))',
                    border: '1px solid rgba(168,130,214,0.25)',
                    color: 'rgba(168,130,214,0.95)',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowLifeNarrative(true); }}
                >
                  📖 Life Narrative
                </div>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(180,140,200,0.2), rgba(180,140,200,0.1))',
                    border: '1px solid rgba(180,140,200,0.25)',
                    color: 'rgba(180,140,200,0.95)',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowAnalyticalPortrait(true); }}
                >
                  🔬 Analytical Portrait
                </div>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(109,188,154,0.15), rgba(59,114,95,0.1))',
                    border: '1px solid rgba(109,188,154,0.2)',
                    color: 'rgba(109,188,154,0.85)',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowPrivacyPanel(true); }}
                >
                  🛡️ Privacy
                </div>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(109,188,154,0.2), rgba(59,114,95,0.15))',
                    border: '1px solid rgba(109,188,154,0.25)',
                    color: 'rgba(109,188,154,0.95)',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowTemporalEvolution(true); }}
                >
                  📈 Soul Evolution
                </div>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(232,213,181,0.15), rgba(200,170,120,0.1))',
                    border: '1px solid rgba(232,213,181,0.25)',
                    color: 'rgba(232,213,181,0.95)',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowFamilyTree(true); }}
                >
                  👨‍👩‍👧‍👦 Family Tree
                </div>
              </div>
              {/* Auto-call toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'rgba(232,213,181,0.7)' }}>
                  <input
                    type="checkbox"
                    checked={aiAutoCallsEnabled}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      try {
                        await fetch(`${api}/update_preferences`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ai_auto_calls_enabled: val }),
                        });
                        setAiAutoCallsEnabled(val);
                      } catch (err) { console.error('Failed to update auto-call pref', err); }
                    }}
                    style={{ width: 16, height: 16, accentColor: '#3b725f', cursor: 'pointer' }}
                  />
                  📞 Allow AI to autonomously call approved family members
                </label>
              </div>
            </div>
          </div>
          
          {/* Data & Export */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <div id="settings-data" style={{ fontSize: 18 }}>Data & Export</div>
            <div style={{ width: '100%', height: 1, marginTop: 8, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }} />
            <div style={{ width: '100%', paddingTop: 15, marginBottom: 25 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, rgba(59,114,95,0.3), rgba(45,90,72,0.2))',
                    border: '1px solid rgba(59,114,95,0.3)',
                    color: 'white',
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => { setShowMenu(false); setShowDataExport(true); }}
                >
                  📦 Export Your Data
                </div>
              </div>
            </div>
          </div>
          
          {/* Account Settings */}
          <div id="settings-account">
            <AccountSettings token={token} api={api} user={user} onLogout={logout} />
          </div>
          
          {/* Enhanced Logout Button */}
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: 30,
            marginBottom: 20,
            padding: "0 25px",
            boxSizing: "border-box"
          }}>
            <div 
              className="convert-white-hover"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px 20px",
                borderRadius: 8,
                cursor: "pointer",
                background: "#393939",
                width: "100%",
                transition: "all 0.2s ease"
              }}
              onClick={logout}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#393939";
              }}
            >
              <IoIosLogOut style={{
                fontSize: 22,
                marginRight: 8
              }} />
              <span style={{
                fontSize: 16
              }}>Log Out</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    )
  }

  return (
    <div
      className="App"
      style={{
        position: "relative",
        background: "transparent",
        height: "100%",
      }}
    >
      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage.message}
          type={toastMessage.type}
          onClose={() => setToastMessage(null)}
        />
      )}
      
      {/* Public Share Banner */}
      {isPublicMode && (
        <div style={{
          position: 'absolute',
          top: isMobile ? 5 : 10,
          left: isMobile ? 5 : 10,
          right: isMobile ? 5 : 10,
          zIndex: 1000,
          backgroundColor: 'rgba(59, 114, 95, 0.9)',
          padding: isMobile ? '6px 12px' : '8px 16px',
          borderRadius: 8,
          textAlign: 'center',
          color: 'white',
          fontSize: isMobile ? 12 : 14,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? 4 : 8
        }}>
          <span style={{ fontWeight: 'bold' }}>🔗 Public Legacy Mode</span>
          <span style={{ opacity: 0.9 }}>
            {isMobile ? 'Shared session' : "You're experiencing a shared Legacy mode session"}
          </span>
        </div>
      )}

      {showIntroMode && <Overlay assetsLoading={!assetsLoaded} isMobile={isMobile} smallerThan850={smallerThan850} isSmallSize={isSmallSize} token={token} showCreateAccount={showCreateAccount} signInWithGoogle={signInWithGoogle} showLoginView={showLoginView} handleStartApp={handleStartApp} toggleLoginView={() => {
          setShowCreateAccount(false)
          setShowLoginView(true)
        }} toggleCreateAccountView={() => {
          setShowLoginView(false)
          setShowCreateAccount(true)
        }} navigateBack={() => {
          setShowCreateAccount(false);
          setShowLoginView(false);
        }}
        setShowPrivacyPolicyDialog={setShowPrivacyPolicyDialog}
        windowWidth={windowWidth}/>}
        {showMenu && renderMenu()}
      {/* Onboarding Flow for first-time users */}
      {showOnboarding && !showIntroMode && token && (
        <Onboarding
          selectedModel={selectedModel}
          onSelectModel={(model) => {
            setSelectedModel(model);
          }}
          onComplete={async () => {
            setShowOnboarding(false);
            setShowModelSelectionScreen(false);
            try {
              await fetch(`${api}/update_preferences`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ onboarding_complete: true }),
              });
              // Also save the model selection so the model overlay doesn't appear
              await updateModel(selectedModel || 'Synth');
            } catch (e) { console.error('Failed to save onboarding state:', e); }
          }}
        />
      )}
      {/* Memory Chat View - shown as default when not in intro mode and not on avatar view */}
      {!showIntroMode && appView === 'memory-chat' && (
        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
        <ErrorBoundary name="Chat">
        <MemoryChatView
          chat={chat}
          onSendMessage={async message => {
            if (wsRef.current) {
              if (typeof message === 'string') {
                setChat(prev => [...prev, {
                  "role": "user",
                  "content": message,
                  "timestamp": Math.floor(Date.now() / 1000)
                }]);
                wsRef.current.send(JSON.stringify({
                  "type": "CHAT_MESSAGE",
                  "message": message
                }));
                setLoadingChat(true);
              } else if (message.type === 'AUDIO_MESSAGE') {
                setChat(prev => [...prev, {
                  "role": "user",
                  "type": "AUDIO_MESSAGE",
                  "audioUrl": message.audioUrl,
                  "timestamp": Math.floor(Date.now() / 1000)
                }]);
                if (message.audioData) {
                  wsRef.current.send(JSON.stringify({
                    "type": "VOICE_MESSAGE",
                    "audio": message.audioData,
                    "format": "webm",
                    "sessionId": sessionId
                  }));
                  setLoadingChat(true);
                }
              }
            }
          }}
          isLoading={loadingChat}
          token={token}
          api={api}
          onDeleteMessage={handleDeleteMessage}
          onPlayEmote={handlePlayMessageEmote}
          isMobile={isMobile}
          assistantTalking={assistantTalking}
          memories={memories}
          highlightedMemories={highlightedMemories}
          onSwitchToAvatar={() => { stopSpeaking(); setAppView('avatar'); }}
          isWsOpen={isWsOpen}
          chatLoading={chatLoading}
          selectedModel={selectedModel}
          onOpenModelSelection={() => setShowModelSelectionScreen(true)}
          onModelChange={updateModel}
          isUpdatingModel={isUpdatingModel}
          autoSpeak={autoSpeak}
          onToggleAutoSpeak={() => setAutoSpeak(prev => !prev)}
          isSpeaking={isSpeaking}
          onStopSpeaking={stopSpeaking}
          speakVolume={speakVolume}
          onVolumeChange={setSpeakVolume}
          speakSpeed={speakSpeed}
          onSpeedChange={setSpeakSpeed}
          proactiveMode={proactiveMode}
          onToggleProactive={() => setProactiveMode(prev => !prev)}
          proactiveStream={proactiveStream}
          faceStatus={faceStatus}
          hasEnrolledFace={hasEnrolledFace}
          onEnrollFace={async (onProgress) => {
            if (!presenceVideoRef.current) return false;
            const ok = await FaceRec.enrollFace(presenceVideoRef.current, 5, 600, onProgress);
            setHasEnrolledFace(FaceRec.hasEnrolledFace());
            return ok;
          }}
          onClearFace={() => {
            FaceRec.clearEnrolledFace();
            setHasEnrolledFace(false);
            setFaceStatus('no-one');
            faceStatusRef.current = 'no-one';
          }}
          onOpenSettings={() => setShowMenu(true)}
          onOpenSoulOrb={() => setShowSoulOrb(true)}
          onOpenLifeNarrative={() => setShowLifeNarrative(true)}
          onOpenAnalyticalPortrait={() => setShowAnalyticalPortrait(true)}
          onOpenPrivacy={() => setShowPrivacyPanel(true)}
          onOpenTemporalEvolution={() => setShowTemporalEvolution(true)}
          onOpenFamilyTree={() => setShowFamilyTree(true)}
          fetchMemories={fetchMemories}
        />
        </ErrorBoundary>
      </div>
      )}
      {!showIntroMode && appView === 'avatar' && <div style={{
        animation: 'fadeIn 0.3s ease-out',
        position: "fixed",
        top: 0,
        right: 0,
        width: "100%",
        height: "100%"
      }}>
                  <div style={{
          position: "relative",
          width: "100%",
          height: "100%"
        }}>
        <BackgroundScene
          currentMicroExpression={currentMicroExpression}
          setCurrentMicroExpression={setCurrentMicroExpression}
          isSmallSize={isSmallSize} isTalking={isTalking}
          assistantTalking={assistantTalking}
          visemeSequence={visemes}
          currentEmote={currentEmote}
          setCurrentEmote={setCurrentEmote}
          isIntroMode={showIntroMode} onModelLoaded={handleModelLoaded} isModelVisible={isModelVisible}

          customAvatarUrl={customAvatarUrl}
          loadingPreferences={loadingPreferences}
          avatarRefreshKey={avatarRefreshKey}
          connectAnimationActive={connectAnimationActive}
          />
        {shouldShowModelLoader() && <ModelLoader />}
      </div>
      </div>}
      {showSaveDialog && (
        <SaveDialog
          onSave={handleSaveConversation} 
          onCancel={() => {
            setShowSaveDialog(false);
            setConversationToSave(null);
          }}
        />
      )}
  {/* Blocking overlay if user is signed in but not email-verified */}
  {!loading && token && user && !user.emailVerified && (
    <EmailVerificationOverlay />
  )}
      {!showIntroMode && appView === 'avatar' && (
      <>
        <div style={{
            position: "absolute",
            top: isMobile ? 10 : "16px",
            left: "16px",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
          <div style={{
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            borderRadius: "21px",
            color: "white",
            fontSize: isMobile ? 19 : "23px",
            width: isMobile ? 120 : 155,
            height: isMobile ? 45 : 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer"
          }}
          onClick={() => {
            window.location.href = '/';
          }}
          >
                          <img
                src="https://ssobjects.blob.core.windows.net/logo/logo.png"
                alt="Heart Felt Souls Logo"
              style={{ 
                height: isMobile ? 20 : 24,
                width: "auto",
                objectFit: "contain"
              }} 
            />
          </div>
          {token && <div
            style={{
              width: isMobile ? 32 : 38,
              height: isMobile ? 32 : 38,
              borderRadius: "50%",
              background: showAvatarBackground ? 'rgba(48, 43, 35, 0.82)' : 'rgba(59, 114, 95, 0.6)',
              border: showAvatarBackground ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(59, 114, 95, 0.4)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              color: "white",
              fontSize: isMobile ? 14 : 16
            }}
            title={showAvatarBackground ? "Hide background" : "Show background"}
            onClick={() => {
              const next = !showAvatarBackground;
              setShowAvatarBackground(next);
              localStorage.setItem('showAvatarBackground', JSON.stringify(next));
            }}
          >
            <FiImage style={{ opacity: showAvatarBackground ? 1 : 0.5 }} />
          </div>}
          {token && !conversing && <div
            style={{
              height: isMobile ? 28 : 32,
              borderRadius: "16px",
              background: 'rgba(48, 43, 35, 0.82)',
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: isMobile ? "4px 10px" : "5px 12px",
                fontSize: isMobile ? 11 : 12,
                color: "rgba(255, 255, 255, 0.5)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                letterSpacing: "0.3px"
              }}
              title="Switch to Memory Chat view"
              onClick={() => { stopSpeaking(); setAppView('memory-chat'); }}
              onMouseOver={(e) => { e.currentTarget.style.color = "white"; e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
              onMouseOut={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.5)"; e.currentTarget.style.background = "transparent"; }}
            >Chat</div>
            <div
              style={{
                padding: isMobile ? "4px 10px" : "5px 12px",
                fontSize: isMobile ? 11 : 12,
                fontWeight: 600,
                color: "#fefcf6",
                background: "rgba(59, 114, 95, 0.55)",
                borderRadius: "14px",
                letterSpacing: "0.3px"
              }}
            >Avatar</div>
          </div>}
        </div>
        {selectedModel && <div
          ref={modelDropdownRef}
          style={{
            position: "absolute",
            top: isMobile ? "58px" : "85px",
            left: "50%",
            transform: !isMobile ? "translate(-50%, -35%)" : "translateX(-50%)",
            background: 'rgba(48, 43, 35, 0.82)',
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "26px",
            padding: isModelDropdownOpen ? "0.8rem" : isMobile ? "3px 16px" : "0.5rem 1.2rem",
            color: "white",
            fontSize: isMobile ? 16 : "1.2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease-in-out",
            height: isModelDropdownOpen ? "auto" : isMobile ? 40 : "48px",
            flexDirection: isModelDropdownOpen ? "column" : "row",
            overflow: "hidden",
            maxHeight: isMobile && isModelDropdownOpen ? "calc(100vh - 200px)" : "none",
            zIndex: 99
          }}
          onClick={() => !isModelDropdownOpen && setIsModelDropdownOpen(true)}
        >
          {!isModelDropdownOpen ? (
            <>
              {selectedModel}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ 
                  marginLeft: "8px",
                  transition: "transform 0.2s ease",
                }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </>
          ) : (
            <>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                width: "100%", 
                marginBottom: "10px",
                alignItems: "center"
              }}>
                <div style={{ fontSize: "1.2rem", flex: 1 }}>{selectedModel}</div>
                <div 
                  style={{ 
                    cursor: "pointer", 
                    fontSize: 18, 
                    width: 24, 
                    height: 24, 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.1)",
                    transition: "background-color 0.2s ease"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsModelDropdownOpen(false);
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                >
                  ×
                </div>
              </div>
              <div style={{ maxHeight: "190px", maxWidth: 280, width: isMobile ? 250 : "100%", overflowY: "auto", fontSize: 15 }}>
                {selectedModel === "Synth" ? <div style={{
                  borderTop: '1px solid grey',
                  borderBottom: '1px solid grey',
                  paddingBottom: 8,
                  paddingTop: 8,
                  fontSize: 14
                }}>
                  Best for exploring your inner self, the connection with the universe and consciousness.
                </div> : <div style={{
                  borderTop: '1px solid grey',
                  borderBottom: '1px solid grey',
                  paddingBottom: 8,
                  paddingTop: 8,
                  fontSize: 14
                }}>
                  Best for relaxed, supportive chats focused on relationships and self-reflection.
                </div>}
                <div style={{width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
                <div style={{
                  width: 95,
                  height: 33,
                  borderRadius: 8,
                  color: "black",
                  fontSize: 14,
                  background: "rgba(255, 255, 255, 0.85)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 8
                }} onClick={() => {
                  setShowModelSelectionScreen(true);
                }}>
                  <span>Change</span>
                </div>
                </div>
              </div>
            </>
          )}
        </div>}

        {/* Persistent crisis banner — self-polls /api/crisis-events/unresolved
            and pins to the top when a managed account needs attention. */}
        {token && (
          <CrisisBanner
            token={token}
            api={api}
            onReview={(ev) => { setCareDashAccount({ id: ev.managed_user_id, name: ev.managed_name }); setCareDashTab('alerts'); }}
          />
        )}

        {/* Unified Care dashboard (slide-in portal). Delegation "view memories
            as them" is intentionally not wired in this stage. */}
        {careDashAccount && (
          <CareDashboard
            token={token}
            api={api}
            initialAccount={careDashAccount}
            initialTab={careDashTab}
            onClose={() => setCareDashAccount(null)}
          />
        )}

        {!loading && !isPublicMode && (
        <div style={{
          position: "absolute",
          top: isMobile ? 10 : token ? "16px" : "22px",
          right: "16px",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
          {/* Stop speaking button */}
          {isSpeaking && token && (
            <button
              className="mcv-settings-btn"
              onClick={stopSpeaking}
              title="Stop speaking"
              style={{ background: 'rgba(189, 123, 105, 0.4)', border: '1px solid rgba(189, 123, 105, 0.3)' }}
            >
              <IoStop />
            </button>
          )}
          {/* Care (managed accounts) heart icon */}
          {token && (
            <CareIcon onOpen={() => { setCareDashAccount({ id: null }); setCareDashTab('overview'); }} />
          )}
          {/* Gear icon + popover */}
          {token ? (
            <div className="mcv-settings-wrapper">
              <button
                ref={avatarSettingsBtnRef}
                className={`mcv-settings-btn ${showAvatarSettings ? 'active' : ''}`}
                onClick={() => setShowAvatarSettings(!showAvatarSettings)}
                title="Settings"
                style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, fontSize: isMobile ? 16 : 18 }}
              >
                <IoSettingsOutline />
              </button>
              {showAvatarSettings && ReactDOM.createPortal(
                <div
                  ref={avatarSettingsPopoverRef}
                  className="mcv-settings-popover"
                  style={avatarSettingsBtnRef.current ? (() => {
                    const r = avatarSettingsBtnRef.current.getBoundingClientRect();
                    return { top: r.bottom + 6, left: Math.max(8, r.right - 240), width: 240 };
                  })() : {}}
                >
                  <div className="mcv-settings-title">Settings</div>
                  {/* AI Voice toggle */}
                  <div className="mcv-settings-row" onClick={() => setAutoSpeak(prev => !prev)}>
                    <div className="mcv-settings-row-left">
                      {autoSpeak ? <HiVolumeUp style={{ fontSize: 14 }} /> : <HiVolumeOff style={{ fontSize: 14 }} />}
                      <span>AI Voice</span>
                    </div>
                    <div className={`mcv-settings-switch ${autoSpeak ? 'on' : ''}`}>
                      <div className="mcv-settings-switch-thumb" />
                    </div>
                  </div>
                  {/* Volume + Speed (shown when autoSpeak is on) */}
                  {autoSpeak && (
                    <div className="mcv-settings-sub">
                      <div className="mcv-volume-row">
                        <span className="mcv-volume-row-label">Vol</span>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={speakVolume}
                          onChange={(e) => setSpeakVolume(parseFloat(e.target.value))}
                          className="mcv-volume-slider"
                        />
                        <span className="mcv-volume-label">{Math.round(speakVolume * 100)}%</span>
                      </div>
                      <div className="mcv-volume-row">
                        <span className="mcv-volume-row-label">Speed</span>
                        <div className="mcv-speed-options">
                          {[0.75, 1, 1.25, 1.5].map(s => (
                            <button
                              key={s}
                              className={`mcv-speed-btn ${speakSpeed === s ? 'active' : ''}`}
                              onClick={() => setSpeakSpeed(s)}
                            >
                              {s}x
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Proactive mode */}
                  <div className="mcv-settings-row" onClick={() => setProactiveMode(prev => !prev)}>
                    <div className="mcv-settings-row-left">
                      <span className={`mcv-proactive-dot-sm ${proactiveMode ? 'active' : ''}`} />
                      <span>Proactive Mode</span>
                    </div>
                    <div className={`mcv-settings-switch ${proactiveMode ? 'on' : ''}`}>
                      <div className="mcv-settings-switch-thumb" />
                    </div>
                  </div>
                  {/* Face Recognition section */}
                  {proactiveMode && (
                    <div className="mcv-settings-sub">
                      <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Face Recognition</div>
                      {hasEnrolledFace ? (
                        <>
                          <div style={{ fontSize: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: faceStatus === 'recognized' ? '#22c55e' : faceStatus === 'stranger' ? '#f59e0b' : faceStatus === 'detected' ? '#3b82f6' : '#6b7280', display: 'inline-block' }} />
                            {faceStatus === 'recognized' ? 'You (recognized)' : faceStatus === 'stranger' ? 'Unknown face' : faceStatus === 'detected' ? 'Face detected' : 'No one detected'}
                          </div>
                          <button
                            className="mcv-face-btn danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              FaceRec.clearEnrolledFace();
                              setHasEnrolledFace(false);
                              setFaceStatus('no-one');
                            }}
                          >Forget my face</button>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.7 }}>
                            Register your face so Legacy can recognize you
                          </div>
                          <button
                            className="mcv-face-btn"
                            disabled={avatarEnrolling || !proactiveStream}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!presenceVideoRef.current) return;
                              setAvatarEnrolling(true);
                              setAvatarEnrollProgress('Starting...');
                              const ok = await FaceRec.enrollFace(presenceVideoRef.current, 5, 600, (cur, total) => setAvatarEnrollProgress(`Capturing ${cur}/${total}...`));
                              setAvatarEnrollProgress(ok ? 'Done!' : 'Failed — try better lighting');
                              setAvatarEnrolling(false);
                              setHasEnrolledFace(FaceRec.hasEnrolledFace());
                              setTimeout(() => setAvatarEnrollProgress(''), 2000);
                            }}
                          >{avatarEnrolling ? avatarEnrollProgress : 'Register my face'}</button>
                        </>
                      )}
                    </div>
                  )}
                  {/* Divider */}
                  <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
                  {/* Full Settings link */}
                  <div
                    className="mcv-settings-row"
                    onClick={() => { setShowAvatarSettings(false); setShowMenu(true); }}
                  >
                    <div className="mcv-settings-row-left">
                      <RxAvatar style={{ fontSize: 14 }} />
                      <span>All Settings</span>
                    </div>
                    <span style={{ fontSize: 12, opacity: 0.4 }}>›</span>
                  </div>
                </div>,
                document.body
              )}
            </div>
          ) : (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: "blur(15px)",
                WebkitBackdropFilter: "blur(15px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "26px",
                color: "black",
                fontSize: isMobile ? 14 : 15,
                fontWeight: "bold",
                height: isMobile ? 45 : 45,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: isMobile ? "0 12px" : "0 16px"
              }}
              onClick={() => { window.location.href = '/'; }}
            >
              Create Account
            </div>
          )}
        </div>
        )}

        {/* Tip Card removed — was permanently disabled (showTipCard always false) */}

        <div style={{
          position: "absolute",
          top: isPublicMode ? (isMobile ? "160px" : "120px") : (isMobile ? "140px" : "100px"),
          left: "16px",
          bottom: isMobile ? "100px" : "20px",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          marginTop: isMobile && !isChatExpanded && !isMemoryExpanded ? "50%" : 0
        }}>
            <CollapsibleMemoriesPanel
              isMobile={isMobile}
              token={token}
              requiresAccount
              memories={memories}
              MemoryCard={MemoryCard}
              title="Memories"
              onClear={handleClearMemories}
              onSaveMemory={handleSaveMemory}
              onManage={() => setShowMemoryManager(true)}
              api={api}
              toggleComponent={token &&
                <Switch
                  isChecked={isMemoryEnabled}
                  onChange={(value) => handleMemoryToggle(value, token)}
                  isDisabled={isTogglingMemory}
                  isLoading={loadingPreferences}
                />
              }
              expanded={isMemoryExpanded}
              toggleExpanded={() => {
                toggleMemoryExpanded(prev => !prev)
              }}
              extraStyles={{
                "maxHeight": isMobile && isChatExpanded ? "50%" : ""
              }}
            >
              {memories &&
                  memories.slice(0, visibleMemoryCount).map((memory, i) => (
                    <MemoryCard key={memory.id || i} memory={memory} token={token} api={api} onDelete={handleDeleteMemory} />
                  ))}
              {memories && memories.length > visibleMemoryCount && (
                <button
                  onClick={() => setVisibleMemoryCount(c => c + MEMORY_PAGE)}
                  style={{
                    width: '100%', padding: '10px 0', marginTop: 8,
                    background: 'rgba(255,255,255,0.06)', color: 'inherit',
                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
                    cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Show more ({memories.length - visibleMemoryCount} remaining)
                </button>
              )}
            </CollapsibleMemoriesPanel>
            <CollapsibleMemoriesPanel
              isMobile={isMobile}
              memories={[]}
              MemoryCard={() => {}}
              title="Chat"
              expanded={isChatExpanded}
              toggleExpanded={() => {
                if (!conversing && !calling) {
                  toggleExpandChat(prev => !prev)
                }
              }}
              onClear={handleClearChat}
              api={api}
              token={token}
              toggleComponent={token &&
                <Switch 
                  isChecked={isChatEnabled}
                  onChange={value => handleChatToggle(value, token)}
                  isDisabled={isTogglingChat}
                  isLoading={loadingPreferences}
                />
              }
              toggleLabel="Save"
              extraStyles={{
                "maxHeight": isMobile && isMemoryExpanded ? "50%" : ""
              }}
            >
              <div style={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 20,
                display: "flex"
              }}>
                <div style={{
                  position: "absolute",
                  width: "100%",
                  height: 1,
                  background: "rgba(255, 255, 255, 0.25)",
                  display: "flex"
                }} />
                {chatLoading || !isWsOpen ?
                <div style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: 15
                }}>
                  <LoadingDiv
                    isLoading 
                    duration={0.75} 
                    width={`${25}px`}
                    height={`${25}px`}
                    borderWidth={1}
                    loadingColor="#FFFFFF"
                    borderColor="rgba(255, 255, 255, 0.5)"
                    borderRadius={`${10}px`}
                    backgroundColor="transparent"
                    loadingSegmentPercentage={25}
                  />
                </div>
                : <Chat chat={chat} onSendMessage={async message => {
                  if (wsRef.current) {
                    // Check if the message is a text message (string) or an audio message (object)
                    if (typeof message === 'string') {
                      // Check if this is a vision-related query
                      const isVisionQuery = (() => {
                        const text = message.toLowerCase();
                        const visionKeywords = [
                          "what do you see", "can you see", "what can you see", "describe what you see",
                          "what's in front of me", "what am i wearing", "how do i look", "what's behind me",
                          "describe my environment", "what's in my room", "what's around me", "look at me",
                          "what do i look like", "can you see me", "do you see", "what's visible",
                          "describe the scene", "what's in the background", "tell me what you see",
                          "what's in view", "analyze what you see", "what's in the image", "visual",
                          "sight", "vision", "observe", "watch", "viewing", "looking at", "appearance",
                          "what's here", "what's there", "show me", "see this", "look around"
                        ];
                        return visionKeywords.some(keyword => text.includes(keyword));
                      })();

                      // If it's a vision query and we're not already on a call with video, activate webcam
                      if (isVisionQuery && !calling) {
                        console.log("🔍 Vision-related query detected, activating webcam...");
                        setPhoneCalling(true);
                        setConnectAnimationActive(true);
                        setTimeout(() => setConnectAnimationActive(false), 3000);
                        await initiateWebRTC(true); // Enable video for vision analysis
                        // Give the webcam a moment to initialize
                        await new Promise(resolve => setTimeout(resolve, 500));
                      }

                      // It's a text message, handle it as before
                      setChat([...chat, {
                        "role": "user",
                        "content": message,
                        "timestamp": Math.floor(Date.now() / 1000)
                      }]);
              
                      wsRef.current.send(JSON.stringify({
                        "type": "CHAT_MESSAGE",
                        "message": message
                      }));
              
                      setLoadingChat(true);
                    } 
                    else if (message.type === 'AUDIO_MESSAGE') {
                      // It's an audio message
                      // Add to chat immediately for UI feedback
                      setChat([...chat, {
                        "role": "user",
                        "type": "AUDIO_MESSAGE",
                        "audioUrl": message.audioUrl,
                        "timestamp": Math.floor(Date.now() / 1000)
                      }]);
              
                      // If the audio message has base64 audio data, send it through WebSocket
                      if (message.audioData) {
                        wsRef.current.send(JSON.stringify({
                          "type": "VOICE_MESSAGE",
                          "audio": message.audioData,
                          "format": "webm",
                          "sessionId": sessionId
                        }));

                        setLoadingChat(true);
                      }
                    }
                  }
                }} isLoading={loadingChat} token={token} api={api} onDeleteMessage={handleDeleteMessage} isMobile={isMobile}
                onPlayEmote={handlePlayMessageEmote} assistantTalking={assistantTalking} selectedModel={selectedModel} />}
              </div>
            </CollapsibleMemoriesPanel>
        </div>
        {!isMobile && conversing &&
          <div 
            style={{
              position: "absolute",
              width: isTranscriptionExpanded ? "290px" : "220px",
              maxHeight: isTranscriptionExpanded ? "250px" : "none",
              bottom: "0px",
              right: "0px",
              display: "flex",
              flexDirection: "column",
              maxWidth: "400px",
              padding: "20px",
              zIndex: 3,
              alignItems: "flex-end",
              justifyContent: "flex-end",
              background: 'linear-gradient(to bottom, rgba(48, 43, 35, 0.75) 0%, rgba(48, 43, 35, 0) 70%, rgba(48, 43, 35, 0.15) 100%)',
              borderTopLeftRadius: 10,
              borderTop: "1px solid rgba(255, 255, 255, 0.12)",
              transition: 'all 0.2s ease',
              paddingTop: 10
            }}
            onMouseEnter={(e) => {
              setExpandTranscription(true);
            }}
            onMouseLeave={(e) => {
              setExpandTranscription(false);
            }}
          >
            <div style={{
              // background: "rgba(30, 30, 30, 0.55)",
              padding: "10px 14px",
              borderRadius: 6,
              // border: "1px solid rgba(80, 80, 80, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}>
              <LuMessagesSquare style={{
                fontSize: 21
              }} />
              <span style={{
                marginLeft: 8,
                fontSize: 16
              }}>Voice Transcriptions</span>
            </div>
            <div style={{
              width: "100%",
              height: 1,
              background: 'linear-gradient(to right, rgba(125, 125, 125, 0) 0%, rgba(180, 180, 180, 1) 50%, rgba(50, 50, 50, 0) 100%)',
            }} />
            <div style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              padding: 8,
              boxSizing: "border-box"
            }}>
              {/* Render messages in the correct order based on which was received last */}
              {userVoiceMessage && assistantMessage ? (
                // Both messages exist
                latestMessageType === 'assistant' ? (
                  // Assistant was the last to speak
                  <>
                    {renderUserMessage(userMessageKey)}
                    {renderAssistantMessage(assistantMessageKey)}
                  </>
                ) : (
                  // User was the last to speak
                  <>
                    {renderAssistantMessage(assistantMessageKey)}
                    {renderUserMessage(userMessageKey)}
                  </>
                )
              ) : (
                // Only one message exists
                <>
                  {assistantMessage && renderAssistantMessage(assistantMessageKey)}
                  {userVoiceMessage && renderUserMessage(userMessageKey)}
                </>
              )}
            </div>
          </div>
        }
        <div style={{
          position: "absolute",
          bottom: isMobile ? "20px" : "50px",
          right: "50%",
          display: "flex",
          transform: "translateX(50%)",
          zIndex: 3,
          flexDirection: "column"
        }}>
          {(
            <div style={{
              height: 25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 10
            }}>
              {conversing  && (userVoiceMessage === "..." || assistantMessage === "...") ? (
                <div style={{
                  background: 'rgba(50, 50, 50, 0.25)',
                  width: "60px",
                  height: "100%",
                  borderRadius: 5
                }}>
                  <LoadingDots size={5} />
                </div>
            ) : null}
            </div>
            )}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <div
              style={{
                zIndex: 2,
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                background: 'linear-gradient(135deg, #3b725f, #4a8a73)',
                border: "1px solid rgba(59, 114, 95, 0.4)",
                borderRadius: "46px",
                color: "white",
                textAlign: "center",
                minWidth: 140,
                minHeight: 60,
                display: "flex",
                alignItems: "center"
              }}
            >
              {!isWsOpen && <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <LoadingDiv
                    isLoading 
                    duration={0.75} 
                    width={`${25}px`}
                    height={`${25}px`}
                    borderWidth={1}
                    loadingColor="#FFFFFF"
                    borderColor="rgba(255, 255, 255, 0.5)"
                    borderRadius={`${10}px`}
                    backgroundColor="transparent"
                    loadingSegmentPercentage={25}
                  />
                </div>}
                {renderConversing()}
              </div>
              {conversing && (
                <div style={{
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    background: 'rgba(0, 0, 0, 0.25)',
                    border: "1px solid rgba(255, 255, 255, 0.4)",
                    padding: 12,
                    borderRadius: 46,
                    marginLeft: 10,
                    cursor: "pointer"
                  }}
                  onClick={handleDisconnect}
                >
                  <LoadingDiv
                    isLoading={disconnecting} 
                    duration={0.75}
                    width={`${isMobile ? 36 : 46}px`}
                    height={`${isMobile ? 36 : 46}px`}
                    borderWidth={1}
                    loadingColor="#FFFFFF"
                    borderColor="rgba(255, 255, 255, 0.5)"
                    borderRadius={`${46}px`}
                    backgroundColor="#ed7878"
                    loadingSegmentPercentage={25}
                  >
                  <HiOutlinePhoneXMark style={{fontSize: isMobile ? 17 : 21}} />
                </LoadingDiv>
              </div>)}
            </div>
          </div>
          {showVideo && <div style={getStylesForVideo()}>
            <video
              id="user-video"
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />
          </div>}
          </>)}
          {showIntroMode && <div style={{
            position: "absolute",
            bottom: 20,
            right: 20,
            background: "rgba(0, 0, 0, 0.5)",
            padding: "5px 10px",
            borderRadius: 8,
            fontSize: 14
          }}>
            Synthetic OS V 1.0 • © 2026 SyntheticSouls.live • <span style={{
              cursor: "pointer"
            }} onClick={() => {
              setShowPrivacyPolicyDialog(true)
            }}>Privacy Policy</span>
          </div>}
          {showPrivacyPolicyDialog && <div style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            top: 0,
            zIndex: 99999,
            background: "rgba(0, 0, 0, 0.45)",
            padding: 25,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <div style={{
              background: "rgba(50, 50, 50, 1)",
              borderRadius: 16,
              maxWidth: 800,
              height: "100%",
              position: "relative",
              paddingTop: 40,
              boxSizing: "border-box",
              paddingRight: 40
            }}>
              <div style={{
                height: "100%",
                overflow: "auto"
              }}>
                <PrivacyPolicy />
              </div>
              <IoCloseCircleOutline onClick={() => {
                setShowPrivacyPolicyDialog(false);
              }} style={{
                fontSize: 32,
                position: "absolute",
                top: 10,
                right: 10,
                cursor: "pointer"
              }} />
            </div>
          </div>}
          {showSoulOrb && (
            <SoulOrb
              token={token}
              api={api}
              onClose={() => setShowSoulOrb(false)}
              onOpenWisdom={() => { setShowSoulOrb(false); setShowWisdomArchive(true); }}
              onSendMessage={(message) => {
                if (wsRef.current && typeof message === 'string') {
                  setChat(prev => [...prev, {
                    "role": "user",
                    "content": message,
                    "timestamp": Math.floor(Date.now() / 1000)
                  }]);
                  wsRef.current.send(JSON.stringify({
                    "type": "CHAT_MESSAGE",
                    "message": message
                  }));
                  setLoadingChat(true);
                }
              }}
            />
          )}
          {showPrivacyPanel && (
            <PrivacyPanel
              token={token}
              api={api}
              onClose={() => setShowPrivacyPanel(false)}
            />
          )}
          {showWisdomArchive && (
            <WisdomArchive
              token={token}
              api={api}
              onClose={() => setShowWisdomArchive(false)}
            />
          )}
          {showModelSelectionScreen && <MyPanelWithWaves
            apiSelectedModel={apiSelectedModel.current}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            updateModel={updateModel}
            isUpdatingModel={isUpdatingModel}
            setShowModelSelectionScreen={setShowModelSelectionScreen}
            windowWidth={windowWidth}
            isMobile={isMobile}
          />}
          {showEnvironmentModal &&
          <EnvironmentModal isMobile={isMobile} api={api} token={token} setShowEnvironmentModal={setShowEnvironmentModal} setSelectedEnv={(env) => { envUserChangedRef.current = true; setSelectedEnv(env); }} setEnvironments={setEnvironments} environments={environments} />}
          
          {/* Voice Cloning Modal */}
          {showVoiceCloningModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              animation: 'fadeIn 0.3s ease'
            }}>
              <div style={{
                width: '90%',
                maxWidth: 750,
                maxHeight: '90vh',
                background: 'linear-gradient(145deg, #1E1F26 0%, #13141A 100%)',
                borderRadius: 20,
                overflow: 'auto',
                position: 'relative',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.7), 0 0 120px rgba(59, 114, 95, 0.1)',
                animation: 'slideUp 0.4s ease'
              }}>
                <button
                  onClick={() => setShowVoiceCloningModal(false)}
                  style={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: 20,
                    zIndex: 1,
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'rotate(90deg)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                    e.currentTarget.style.transform = 'rotate(0deg)';
                  }}
                >
                  ×
                </button>
                <VoiceCloning
                  api={api}
                  token={token}
                  showToast={showToast}
                  currentVoiceId={customVoiceId}
                  onSelectVoice={(voiceId) => {
                    handleCustomVoiceUpdate(voiceId);
                    setShowVoiceCloningModal(false);
                  }}
                  onVoiceCloned={(voiceId) => {
                    handleCustomVoiceUpdate(voiceId);
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Webcam Indicator Component - only in avatar view */}
          {appView === 'avatar' && (
            <WebcamIndicator
              isVisionEnabled={isVisionEnabled}
              onToggleVision={handleToggleVision}
              visionAnalysis={visionAnalysis}
              showVideo={sessionId && !showIntroMode}
            />
          )}
          
          {/* Memory Manager Modal */}
          <ErrorBoundary name="Memory Manager">
          <MemoryManager 
            token={token}
            api={api}
            isActive={showMemoryManager}
            onClose={() => setShowMemoryManager(false)}
          />
          </ErrorBoundary>
          
          {/* Conversation Manager Modal */}
          <ConversationManager
            isOpen={showConversationManager}
            onClose={() => setShowConversationManager(false)}
            token={token}
            api={api}
          />
          
          {/* Data Export Modal */}
          <DataExport
            isOpen={showDataExport}
            onClose={() => setShowDataExport(false)}
            token={token}
            api={api}
          />
          
          {/* Life Narrative Modal */}
          <LifeNarrative
            isOpen={showLifeNarrative}
            onClose={() => setShowLifeNarrative(false)}
            token={token}
            api={api}
          />
          
          {/* Analytical Portrait Modal */}
          <AnalyticalPortrait
            isOpen={showAnalyticalPortrait}
            onClose={() => setShowAnalyticalPortrait(false)}
            token={token}
          />
          
          {/* Temporal Evolution Modal */}
          <TemporalEvolution
            isOpen={showTemporalEvolution}
            onClose={() => setShowTemporalEvolution(false)}
            token={token}
          />
          
          {/* Family Tree Modal */}
          <FamilyTree
            isOpen={showFamilyTree}
            onClose={() => setShowFamilyTree(false)}
            token={token}
            api={api}
          />
          
          {/* Share Link Creator Modal */}
          <ShareLinkCreator
            isOpen={showShareLinkCreator}
            onClose={() => setShowShareLinkCreator(false)}
            onCreate={(data) => {
              setShowShareLinkCreator(false);
              fetchShareLinks();
            }}
            token={token}
            api={api}
            memories={memories}
          />
          
      </div>
  );
}

export default App;
